/*
tslint:disable:import-spacing no-inferrable-types
variable-name align no-trailing-whitespace component-class-suffix
no-string-literal component-selector
*/

import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  Output
}                                   from '@angular/core';

import {
  DecisionPoint,
  SwitchingLayout
}                                   from '@/models/scenario/decision.point';
import { Movement }                 from '@/models/scenario/movement';
import {
  DelayType,
  MovementDelay
}                                   from '@/models/scenario/movement.delay';
import { MovementHistory }          from '@/models/scenario/movement.history';
import {
  Train,
  TrainType
}                                   from '@/models/scenario/train';
import { TrainDirection }           from '@/models/scenario/train.class';
import {
  Track,
  TrackType
}                                   from '@/models/scenario/track';
import {
  InterLink,
  InterLinkType
}                                   from '@/models/scenario/inter.link';
import { Scenario }                 from '@/models/scenario/scenario';
import { Logger }                   from '@/util/logger';

import { ViewMemento }              from './view.memento';
import { ViewerInterface }          from './viewer.interface';
import { SelectionManager }         from './selection.manager';
import { SearchManager }            from './search.manager';
import { DistanceAxis }             from './distance.axis';
import { TimeAxis }                 from './time.axis';
import { DelayReportDialog }        from './delay.report.dialog';
import { ConsistReportDialog }      from '@/screens/ide/stringviewer/consist.report.dialog';
import { IdeService }               from '@/services/ide.service';
import { UserLogViewerMetadata }    from '@/screens/ide/user.log.viewer.metadata';
import { Subscription }             from "rxjs";
import {
  TimeCursor,
  TimeCursorMode
}                                   from "@/screens/ide/stringviewer/time.cursor";
import {
  InterLinkPark,
  ParkabilityManager
}                                   from "@/screens/ide/stringviewer/parkability.manager";
import {
  AuthorityManager,
  InterLinkAuth
}                                   from "@/screens/ide/stringviewer/authority.manager";
import { UserLogService }           from "@/services/user.log.service";
import {
  PanZoom,
  PanZoomOptions
}                                   from "@/models/viewer/pan.zoom";
import { ViewerEvent }              from "@/models/viewer/viewer.event";
import { ViewerEventType }          from "@/models/viewer/viewer.event.type";
import { MeshLineBuilder }          from "@/helpers/lines/mesh.line.builder";
import { TrainLinesBuilder }        from "@/screens/ide/stringviewer/train.lines.builder";
import { WorkBlocksBuilder }        from "@/screens/ide/stringviewer/work.blocks.builder";
import { GridBuilder }              from "@/screens/ide/stringviewer/grid.builder";
import { UserLoggingBuilder }       from "@/screens/ide/stringviewer/user.logging.builder";
import { ConnectionViewerMetadata } from "@/screens/ide/connection.viewer.metadata";
import { CrossingsBuilder }         from "@/screens/ide/stringviewer/crossings.builder";

import * as THREE                   from 'three';

import {
  Box2,
  Color,
  Group,
  LineSegments,
  Mesh,
  PerspectiveCamera,
  Plane,
  Scene,
  Vector2,
  WebGLRenderer
}                                   from 'three';

import * as $                       from 'jquery';

import 'gasparesganga-jquery-loading-overlay'; /* https://gasparesganga.com/labs/jquery-loading-overlay/ */

import 'jquery-contextmenu';

export enum StringColorMode {
  TRAIN   = 0,
  TRACK   = 1,
  GENERIC = 2,
  UNKNOWN = 3
}

@Component({
  selector: 'three-string-viewer',
  templateUrl: 'three.string.viewer.html'
})

/**
 *
 * @class ThreeStringViewer
 *
 * @brief re-implementation of the stringviewer, but not in Plotly and not PIXI.  Three.JS is expected to
 * deliver a lot more performance and customization.
 *
 * Some useful links:
 *
 *   Great starting tutorial for doing data graphs in Three.js
 *
 *     @link https://observablehq.com/@grantcuster/using-three-js-for-2d-data-visualization
 *
 *   Line segments in a buffered geometry:
 *
 *     @link https://stackoverflow.com/questions/19221527/three-js-how-to-draw-a-discontinuous-line-using-buffergeometry
 *
 *   Fitting the camera to some geometry:
 *
 *     @link https://stackoverflow.com/questions/14614252/how-to-fit-camera-to-object
 *     @link https://discourse.threejs.org/t/camera-zoom-to-fit-object/936/23
 *
 * Reworked in May 2023 to use the new style PanZoom controller; same one we use in the independant
 * string line viewer (an AgGrid style Angular component you can just use use as a library).
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      November 2019
 *
 */
export class ThreeStringViewer implements AfterViewInit, ViewerInterface {

  /** the observable for limit cursor updates (position/mode toggle) */
  private _limitSub: Subscription               = null;

  /* the axis components */

  /** @var _yAxis DistanceAxis mileposts */
  private _yAxis: DistanceAxis                  = null;

  /** @var _xAxis TimeAxis time scale */
  private _xAxis: TimeAxis                      = null;

   /* scenario modeling */

  /** @var _scenario (maybe timezone specific) scenario */
  private         _scenario: Scenario           = null;

  /** @var _utcCcenario the raw/original scenario model */
  private         _utcScenario: Scenario        = null;

  /* timezone support, basically allow them to offset from UTC */

  private _timezone: string = 'UTC';

  /* rendering pipeline */

  /** @var __drawingSurface the DOM element we draw on (an HTML5, WebGL enabled <canvas> */
  private   _drawingSurface: HTMLCanvasElement|null  = null;

  /** @var _renderer WebGLRenderer the rendering pipeline */
  private         _renderer: WebGLRenderer      = null;

  private    _clippingPlane: Plane              = new Plane( new THREE.Vector3( -1, 0, 0 ) );
  private            _clipX: number             = 0;

  /** @var _camera PerspectiveCamera the camera model */
  private           _camera: PerspectiveCamera  = null;

  /** @var _scene Scene the geometry we pushed to the GPU (z=0.000) */
  private            _scene: Scene              = null;

  /** @var _distanceScale number the geometric scale of the graph (vertical) */
  private    _distanceScale: number             = 1.0;

  /** @var _timeScale number the geometric scale of the graph (horizontal) */
  private        _timeScale: number             = 1.0;

  /** @var _dataX  umber, where the X data points start */
  private            _dataX: number             = 0;

  /** @var _dataY  umber, where the Y data points start */
  private            _dataY: number             = 0;

  /** @var _dataWidth number, the unscaled max time value */
  private        _dataWidth: number             = 0;

  /** @var _dataHeight number, the unscaled max distance value */
  private       _dataHeight: number             = 0;

  /** @var _bound Box2 current scene bounds */
  private            _bound: Box2               = null;

  /** @var _currentFocus Box2 the most recent  world bound we focused on */
  private     _currentFocus: Box2               = null;

  /** @var _noAnimate boolean disable animation when it causes performance issues */
  private        _noAnimate: boolean            = false;

  /* the geometry on the GPU */

  /** @var _trainLines LineSegments the train movement lines */
  private       _trainLines: LineSegments       = null;

  /** @var _crossings LineSegments the diamond crossing lines */
  private       _crossings: LineSegments        = null;

  /** @var _gridLines24 LineSegments the 24 hour based background grid lines */
  private      _gridLines24: LineSegments       = null;

  /** @var _gridCurrent LineSegments the current grid being shown (z=-0.001) */
  private      _gridCurrent: LineSegments       = null;

  /** @var _userLogBounds LineSegments if user logging is on, we show the logging range */
  private      _userLogBounds: LineSegments     = null;

  /** @var _workLines LineSegements the zones for active work */
  private        _workLines: LineSegments       = null;

  /** @var _isFullScreen boolean are we currently in full screen mode? */
  private     _isFullScreen: boolean            = false;

  /** @var viewLocked boolean do we have a default view position/zoom level? */
  public viewLocked: boolean                    = false;

  /** the observable for when one of the string line viewers locks the view position. */
  private viewLockedPosition: Subscription;

  /* selection support */

  /** @var _selectObject Group temp layer for selection overlay graphics (z=0.001) */
  private     _selectObject: Group              = null;
  private _selectionManager: SelectionManager   = null;

  /** @var _selectObject Group temp layer for search overlay graphics (z=0.002) */
  private     _searchObject: Group              = null;
  private    _searchManager: SearchManager      = null;

  /* hover support */

  /** @var _hoverObject Group temp layer for hovering graphics (z=0.002) */
  private      _hoverObject: Group              = null;

  /** @var _limitMode the current "extras" information mode; stuff shown after the clip line */
  private      _limitMode: TimeCursorMode       = TimeCursorMode.OFF;

  /** @var _authorityManager helper for drawing the authority slots */
  private _authorityManager: AuthorityManager   = null;

  /** @var _authoritiesObject Group temp layer for authorities (z=0.004) */
  private _authoritiesObject: Group             = null;
  private _authoritiesObjectHover: Group        = null;

  private    _authoritiesWT: number             = -1;
  private _authoritiesThickness: number         = 50;

  /** @var _parkabilityManager helper for drawing the parkability slots */
  private _parkabilityManager: ParkabilityManager = null;

  /** @var _parkabilityObject Group temp layer for parkability  (z=0.005) */
  private _parkabilityObject: Group            = null;

  private  _parkabilityMesh: Mesh[]            = [];
  private    _parkabilityWT: number            = -1;
  private  _parkabilityThickness: number       = 50;

  /** @var _hoverPad number how much fuzz tot add (in pixels) for being over a line */
  private         _hoverPad: number            = 7;

  /** @var _hoverMove the current train movement being hovered over (null means not over a move */
  private        _hoverMove: Movement          = null;

  /** @var _hoverDecisionPoint the current decision point being hovered over (null means not over one) */
  private   _hoverDecisionPoint: {dp: DecisionPoint, train: Train} = null;

  /** @var _hoverEvent MouseEvent the mouse recent hover event */
  private       _hoverEvent: MouseEvent        = null;

  /* screen space */

  /** @var _height number the current height of the canvas */
  private           _height: number            = 0;

  /** @var _width number the current width of the canvas */
  private            _width: number            = 0;

  /* camera settings */

  /** @var _near number the near plane (z) for the camera (can't be zero for perspective camera) */
  private             _near: number            = 0.01;

  /** @var _far number the far plane (z) for the camera */
  private              _far: number            = 1000;

  /** new style pan/zoom support */
  private _panZoom: PanZoom|null               = null;

  /* animation */

  /** @var _mouseCanvasY number current mouse X position in canvas co-ords */
  private     _mouseCanvasX: number            = 0;

  /** @var _mouseCanvasY number current mouse Y position in canvas co-ords */
  private     _mouseCanvasY: number            = 0;

  /* preferences */

  /** @var spikeLines boolean are we showing the cross-hairs? */
  private       _spikeLines: boolean           = true;
  private      _spikeOffset: number            = 0;

  private         _gridMode: boolean           = true;

  /** @var _nightMode boolean are we in dark or light mode? */
  private        _nightMode: boolean           = true;

  /** @var _colorModde StringColorMode style of track coloring we are using */
  private        _colorMode: StringColorMode   = StringColorMode.TRAIN;

  /** @var _simpleHUD boolean are we showing de-cluttered version of HUD? */
  private        _simpleHUD: boolean           = false;

  /** if there are any problems loading the string line, give the user some feedback */
  public loadError: string                    = '';

  /* SN-1410 Track colors */
  private _trackColorPrefs:Map<string, Array<string>> =
    new Map([
      ["M0", ['#ffffff','#000000']],
      ["M1", ['#ffffff','#000000']],
      ["M2", ["#ff0000",'#ff0000']],
      ["M3", ['#7FFF00','#7FFF00']],
      ["M4", ["#8A2BE2",'#8A2BE2']],
      ["M5", ['#D2691E','#D2691E']],
      ["M6", ["#1E90FF",'#1E90FF']],
      ["P1", ['#0000FF','#0000FF']],
      ["P2", ["#006400",'#006400']],
      ["P3", ['#DAA520','#DAA520']],
      ["P4", ["#20B2AA",'#20B2AA']],
      ["P5", ['#FFDAB9','#FFDAB9']],
      ["P6", ["#C0C0C0",'#C0C0C0']],
    ]);

  private            _prefs: any               = {
    hud: {
      fontSize: 12,
      centerPadX: 20,
      centerPadY: 20
    },
    night: {
      background: '#0d0d21',
      spike: {
        color: '#ffff00',
        width: .3,
        alpha: 1
      },
      workColor: {
        megablock:    '#FFA500',
        construction: '#8A2BE2'
      },
      trackColor: {
        default: '#ffffff',
        doubleSouth: '#ffffff',
        doubleNorth: '#ff0000',
        terminal: '#ffa500',
        junction: '#ff66ff',
        track0: '#ffffff',
        track1: '#0000ff',
        track2: '#0066ff',
        track3: '#3399ff',
        track4: '#3399ff',
        track5: '#ccffff',
        track6: '#ccffcc',
        track7: '#ccff99',
        track8: '#ccff33',
        track9: '#cccc00',
        track10: '#cc9900',
        track11: '#996600'
      },
      trainColor: {
        default: '#ffffff',
        trainA: '#ffffff',
        trainM: '#ffffff',
        trainS: '#228b22',
        trainB: '#228b22',
        trainC: '#228b22',
        trainU: '#228b22',
        trainG: '#228b22',
        trainP: '#00FFFF',
        trainL: '#808080',
        trainK: '#808080',
        trainR: '#808080',
        trainQ: '#ffa500',
        trainZ: '#ff0000'
      }
    },
    day: {
      background: '#ffffff',
      spike: {
        color: '#000000',
        width: .3,
        alpha: 1
      },
      workColor: {
        megablock:    '#FFA500',
        constrution: '#8A2BE2'
      },
      trackColor: {
        default: '#000000',
        doubleSouth: '#000000',
        doubleNorth: '#ff0000',
        terminal: '#ffa500',
        junction: '#ff66ff',
        track0: '#000000',
        track1: '#0000ff',
        track2: '#0066ff',
        track3: '#3399ff',
        track4: '#3399ff',
        track5: '#ccffff',
        track6: '#ccffcc',
        track7: '#ccff99',
        track8: '#ccff33',
        track9: '#cccc00',
        track10: '#cc9900',
        track11: '#996600'
      },
      trainColor: {
        default: '#000000',
        trainA: '#000000',
        trainM: '#000000',
        trainB: '#228b22',
        trainC: '#228b22',
        trainU: '#228b22',
        trainG: '#228b22',
        trainP: '#00FFFF',
        trainL: '#808080',
        trainK: '#808080',
        trainR: '#808080',
        trainQ: '#ffa500',
        trainZ: '#ff0000'
      }
    }
  };

  @Output() movementHover  = new EventEmitter<{move: Movement, event: MouseEvent}>();
  @Input() uuid: number    = 0;

  @Output() parkingHover   = new EventEmitter<{link: InterLinkPark, allowed: boolean, event: MouseEvent}>();

  @Output() authorityHover = new EventEmitter<{link: InterLinkAuth, east: boolean, event: MouseEvent}>();


  constructor(
    private elementRef: ElementRef,
    private ngZone:     NgZone,
    private ide:        IdeService,
    private userlog:    UserLogService
  ) {

    window[`threestring_${this.uuid}`]  = {component: this, zone: ngZone};

    this._selectionManager              = new SelectionManager(this, this.uuid);
    this._searchManager                 = new SearchManager(this, this.uuid);

    this._parkabilityManager            = new ParkabilityManager(this, null, this.uuid);
    this._authorityManager              = new AuthorityManager(this, null, this.uuid, this.userlog);
  }

  /** fetch the widget width */
  width(): number {

    return this._width;
  }

  /** fetch the widget height */
  height(): number {

    return this._height;
  }

  /** fetch the start of the graph (horizontal) */
  dataX(): number {

    return this._dataX;
  }

  /** fetch the start of the graph (vertical) */
  dataY(): number {

    return this._dataY;
  }

  /** fetch the scaling of the graph (horizontal) */
  timeScale(): number {

    return this._timeScale;
  }

  /** fetch the scaling of the graph (vertical) */
  distanceScale(): number {

    return this._distanceScale;
  }

  /* - - - - - - AfterViewInit - - - - - - - - */

  /** @brief first time setup of view */
  ngAfterViewInit() {

    Logger.debug('[threeview]', 'view init...');

    this.clear();

    /* track when any one of the string line viewers locks or unlocks the default view position */
    this.viewLockedPosition = this.ide.activeStringLineView.subscribe((memento: ViewMemento): void => {

      this.viewLocked = !!memento;

    });

    /* set up the selection manager drop down. */

    this._selectionManager.finalize(`selectionmanagerbutton_${this.uuid}`, `selectionmanagerbuttonpopup_${this.uuid}`);

    this._searchManager.finalize(`searchmanagerbutton_${this.uuid}`, `searchmanagerbuttonpopup_${this.uuid}`);

    /* set up the right click context menu */

    // @ts-ignore
    $.contextMenu({

      selector: `#three-string-viewer-canvas_${this.uuid}`,

      autoHide: true,
      reposition: false,
      hideOnSecondTrigger: true,
      zIndex: 30000,
      selectableSubMenu: true,
      className: 'data-title',
      events: {

        hide: () => {

          this._noAnimate = false;

          this.animate();
        },

        show: (options) => {

          /*
           * in full screen mode we can only see children of the thing that is full screen, by default context
           * menu is a child of 'body'.  We can't just invoke contextMenu() on the right <div>, because it calls
           * the other contextmenu function already in jQuery.
           *
           */

          $('.data-title').detach().appendTo(`#three-string-viewer-container_${this.uuid}`);

          /* they may have right-clicked, moved, and right-clicked again, so always update our hover position */

          this.onMouseMoved(window.event as MouseEvent);

          /* turn off the main viewer animation until we're we're done selecting an action */
          this._noAnimate = true;

          /* set the menu title per the move we are over (if there is one) */

          const move: Movement = this.mouseOverMovement();

          let title = '<No Movement>';

          if (move != null) {

            Logger.debug('[contextmenu]', 'showing: ' + move._parent._train._name);

            title = move.quickTitle();

          }

          $('.data-title').attr('data-menutitle', title);

          $(options.$menu).css('width', '300px');
        }
      },

      build: ($trigger, e: any) => {

        if (typeof e.originalEvent === 'undefined') {

          return;
        }

        this.onMouseMoved(e.originalEvent as MouseEvent);

        this.drawHUD();

        const       move: Movement = this.mouseOverMovement();
        const  nightIcon: string   = 'fas fa-adjust';
        const   hudLabel: string   = 'Simple/Full HUD';
        const    hudIcon: string   = 'fas fa-low-vision';
        let    fullLabel: string   = 'Show Full Screen';
        let     fullIcon: string   = 'fas fa-window-maximize';
        let   nightLabel: string   = 'Light Mode';
        let   colorLabel: string   = 'Track Color Mode';
        let    colorIcon: string   = 'fas fa-star';
        const    ulLabel: string   = 'User Log'
        const     ulIcon: string   = 'fas fa-sitemap';
        let  consistIcon: string   = 'fas fa-cubes';
        let consistLabel: string   = 'Consist Report...';

        if (this._isFullScreen) {

          fullLabel = 'Exit Full Screen';
          fullIcon  = 'fas fa-window-minimize';
        }

        if (!this._nightMode) {

          nightLabel = 'Dark Mode';
        }

        if (this._colorMode !== StringColorMode.TRAIN) {

          colorLabel = 'Train Color Mode';
          colorIcon  = 'fas fa-star-o';

        }

        if (move != null) {

          let dp:       DecisionPoint = move._dp;
          let train:    Train         = move._parent._train;
          let depature: number        = move._departure;

          let userLogging: boolean    = this._scenario.hasUserLogging();
          let consist: boolean        = move._parent._consistChanges.length > 0;

          /*
           * we only do user logging if the there was even user logging enabled for this
           * simulation.  But further, we only do it inside the range of time, in which
           * user logging was enabled.  It can't be the whole thing, that would be
           * an impossibly large data set for user logging events.
           *
           */

          if (userLogging) {

            let startSec  = this._scenario.traceStartSeconds();
            let endSec    = this._scenario.traceEndSeconds();

            if ((depature < startSec) || (depature > endSec)) {

              userLogging = false;
            }
          }

          return {

            callback: (key) => {

              Logger.debug('[contextmenu]', 'clicked: ' + key);

              this.menuAction(key, move);
            },

            items: {
                   delays: {name: 'Delay Report', icon: 'fas fa-clock-o'},
                  consist: {name: consistLabel, icon: consistIcon, disabled: !consist},
                     sep1: '---------',
                  dpStart: {
                             name: `${dp._name}...`,
                            "items": {
                               connections: {
                                 name: `Connections...`,
                                 icon: `fas fa-project-diagram`,
                                 callback: (itemKey, opt, e): void => {

                                   this.ide.launchWindow(
                                     new ConnectionViewerMetadata(
                                       '',
                                       '',
                                       this._scenario,
                                       dp._name
                                     ));
                                 },
                                 disabled: false
                               },
                               userlogfocus: {
                                 name: `UserLog Focus Here`,
                                 icon: `fas fa-sitemap`,
                                 callback: (itemKey, opt, e) => {

                                   /* launch */

                                   this.ide.refocus(
                                     new UserLogViewerMetadata(
                                       '',
                                       this._scenario.tag(),
                                       this._scenario.userLogURL(),
                                       this._scenario.userLog(),
                                       this._scenario.tag(),
                                       train,
                                       dp
                                     ),
                                     false
                                   );
                                 },
                                 disabled: !userLogging
                               },
                               userlogcomp: {
                                 name: `UserLog Compare`,
                                 icon: `fas fa-sitemap`,
                                 callback: (itemKey, opt, e) => {

                                   /* launch */

                                   this.ide.refocus(
                                     new UserLogViewerMetadata(
                                       '',
                                       this._scenario.tag(),
                                       this._scenario.userLogURL(),
                                       this._scenario.userLog(),
                                       this._scenario.tag(),
                                       train,
                                       dp
                                     ),
                                     true
                                   );
                                 },
                                 disabled: !userLogging
                               },
                               userlognewtab: {
                                 name: `UserLog In New Tab`,
                                 icon: `fas fa-sitemap`,
                                 callback: (itemKey, opt, e) => {

                                   /* launch */

                                   this.ide.launchWindow(
                                     new UserLogViewerMetadata(
                                       '',
                                       this._scenario.tag(),
                                       this._scenario.userLogURL(),
                                       this._scenario.userLog(),
                                       this._scenario.tag(),
                                       train,
                                       dp
                                     )
                                   );
                                 },
                                 disabled: !userLogging
                               }
                             }
                           },
                     sep2: '---------',
               fullscreen: {name: fullLabel,  icon: fullIcon},
                colormode: {name: colorLabel, icon: colorIcon},
                nightmode: {name: nightLabel, icon: nightIcon},
                  hudmode: {name: hudLabel,   icon: hudIcon}
            }
          };

        }

        return {

          callback: (key) => {

            Logger.debug('[contextmenu]', 'clicked: ' + key);

            this.menuAction(key, move);
          },

          items: {
            fullscreen: {name: fullLabel,  icon: fullIcon},
             colormode: {name: colorLabel, icon: colorIcon},
             nightmode: {name: nightLabel, icon: nightIcon},
               hudmode: {name: hudLabel,   icon: hudIcon}
          }
        };
      }
    });

    /* support for full screen mode */

    const elem = document.getElementById(`three-string-viewer-container-wrapper_${this.uuid}`);
    elem.addEventListener('fullscreenchange', () => {

      this._isFullScreen = document.fullscreenElement != null;

      Logger.debug('[fullscreen]', '(elem) flipped: ', this._isFullScreen);

    }, true);

    /* add behaviors for timescale control */

    {
      const picker: string = `
       <div style="display: inline-block; float: right; margin: 0; padding: 0; width: 120px; height: 28px;">
          <select
          data-style="btn-primary"
          data-header="Time Granularity"
          show-tick
          id="timescale-picker_${this.uuid}"
          class="timescale-picker"
          style="display: block; float: left; width: 120px; height: 28px; margin-top: 2px; font-size: 10px; ">

            <option data-tag="auto"     value="0"  selected="selected">Automatic</option>

            <option data-tag="min"      value="1"  >1 Minute</option>
            <option data-tag="min5"     value="2"  >5 Minute</option>
            <option data-tag="min15"    value="3"  >15 Minute</option>
            <option data-tag="halfhour" value="4"  >Half Hour</option>
            <option data-tag="hour"     value="5"  >1 Hour</option>
            <option data-tag="hour2"    value="6"  >2 Hour</option>
            <option data-tag="hour4"    value="7"  >4 Hour</option>
            <option data-tag="hour6"    value="8"  >6 Hour</option>
            <option data-tag="hour12"   value="9"  >12 Hour</option>
            <option data-tag="day"      value="10" >1 Day</option>
            <option data-tag="day2"     value="11" >2 Day</option>
            <option data-tag="week"     value="12" >1 Week</option>
            <option data-tag="week2"    value="13" >2 Week</option>
            <option data-tag="month"    value="14" >1 Month</option>
          </select>
</div>`;

      $(`#three-string-command-bar_${this.uuid}`).append(picker);
    }

    /*
     * add in a picker for timezone conversion, by default we use UTC, but we allow
     * users to cast to other timezones.
     *
     *   https://en.wikipedia.org/wiki/List_of_tz_database_time_zones
     *
     */

    {
      const tzpicker: string = `
       <div style="display: inline-block; float: right; padding: 0; width: 120px; height: 28px; margin: 0 8px 0 0;">
          <select
          data-style="btn-primary"
          data-header="Timezone"
          show-tick
          id="tz-picker_${this.uuid}"
          class="timescale-picker"
          style="display: block; float: left; width: 120px; height: 28px; margin-top: 2px; font-size: 10px;">
            <option data-tag="UTC"                   value="UTC"  selected="selected" >UTC</option>
            <option data-tag="Canada/Newfoundland"   value="Canada/Newfoundland"      >Newfoundland -3:30/-2:30</option>
            <option data-tag="Canada/Atlantic"       value="Canada/Atlantic"          >Atlantic -4/-3</option>
            <option data-tag="Canada/Eastern"        value="Canada/Eastern"           >Eastern -5/-4</option>
            <option data-tag="Canada/Central"        value="Canada/Central"           >Central -6/-5</option>
            <option data-tag="Canada/Saskatchewan"   value="Canada/Saskatchewan"      >Saskatchewan -6/-6</option>
            <option data-tag="Canada/Yukon"          value="Canada/Yukon"             >Yukon -7/-7</option>
            <option data-tag="Canada/Mountain"       value="Canada/Mountain"          >Mountain -7/-6</option>
            <option data-tag="Canada/Pacific"        value="Canada/Pacific"           >Pacific -8/-7</option>
          </select>
      </div>`;

      $(`#three-string-command-bar_${this.uuid}`).append(tzpicker);
    }

    /* add in x scaling */

    {
      const html = `
 <div style="display: inline-block; float: right; padding: 0; margin: 0 12px 0 0;height: 28px;">
  <i class="fas fa-arrows-h xscale-picker-label night" id="xscale-picker-label_${this.uuid}" aria-hidden="true"></i>
  <input id="xscale-picker_${this.uuid}" class="xscale-picker" min="1" max="1000" >
 </div>
      `;

      $(`#three-string-command-bar_${this.uuid}`).append(html);

      $(`#xscale-picker_${this.uuid}`).change(() => {

        let value: string = $(`#xscale-picker_${this.uuid}`).val().toString();

        value = value.replace(/[^\d.]/g, '');

        $(`#xscale-picker_${this.uuid}`).val(value);

        this.scaleTimeTo(parseFloat(value));

      });
    }

    /* add in y Scaling */

    {
      const html = `
 <div style="display: inline-block; float: right; padding: 0; margin: 0 12px 0 0;height: 28px;">
  <i class="fas fa-arrows-v yscale-picker-label night" id="yscale-picker-label_${this.uuid}" aria-hidden="true"></i>
  <input id="yscale-picker_${this.uuid}" class="yscale-picker" min="1" max="1000" >
 </div>
      `;

      $(`#three-string-command-bar_${this.uuid}`).append(html);

      $(`#yscale-picker_${this.uuid}`).change(() => {

        let value: string = $(`#yscale-picker_${this.uuid}`).val().toString();

        value = value.replace(/[^\d.]/g, '');

        $(`#yscale-picker_${this.uuid}`).val(value);

        this.scaleDistanceTo(parseFloat(value));

      });
    }

    $(`#timescale-picker_${this.uuid}`).on('change', () => {

      if (this._xAxis == null) {

        /* if there is no scenario yet, and no axis..we can't change it */

        $(`#timescale-picker_${this.uuid}`).val($(`#timescale-picker_${this.uuid} option:first`).val());
      }

      const val: string = $(`#timescale-picker_${this.uuid} option:selected`).data('tag');

      this.setTimeScale(val);
    });

    $(`#tz-picker_${this.uuid}`).on('change', () => {

      if (this._xAxis == null) {

        /* if there is no scenario yet, and no axis...we can't change it */

        $(`#tz-picker_${this.uuid}`).val($(`#tz-picker_${this.uuid} option:first`).val());
      }

      const val: string = $(`#tz-picker_${this.uuid} option:selected`).data('tag');

      this.setTimezone(val);
    });
  }

  /** force the timezone (by default its presumed to be UTC */
  public setTimezone(val) {

    if (val == this._timezone) {

      /* already set */

      return ;
    }

    /* set the timezone */

    Logger.debug('[setTimezone] new TZ: ', val);

    this._timezone = val;

    if (this._xAxis == null) {

      /* nothing to refresh yet */

      return ;
    }

    this.refresh();
  }

  /** @brief force the timescale granularity (horizontal axis) */
  public setTimeScale(val) {

    if (this._xAxis == null) {

      return ;
    }

    this._xAxis.setScale(val);
    this._xAxis.update();
  }

  /** @brief helper to flip full screen mode */
  public toggleFullScreen() {

    if (this._isFullScreen) {

     Logger.debug('[fullscreen] exiting full screen...');

      document.exitFullscreen();

    } else {

      Logger.debug('[fullscreen] going full screen...');

      const elem = document.getElementById(`three-string-viewer-container-wrapper_${this.uuid}`);

      if (elem.requestFullscreen) {

        elem.requestFullscreen();
      }
    }
  }

  menuAction(key: string, move: Movement): void {

    switch (key) {

      case 'fullscreen': {
        this.toggleFullScreen();
        break;
      }

      case 'nightmode': {

        this.onNightModeToggle();
        break;
      }

      case 'colormode': {

        this.onColorModeToggle();
        break;
      }

      case 'hudmode': {

        this.onHUDToggle();
        break;
      }

      case 'delays': {

        DelayReportDialog.show(move, this.uuid);
        break;
      }

      case 'consist': {
        ConsistReportDialog.show(move, this.uuid);
        break;
      }

    }

  }

  /* - - - - - - ViewInterface - - - - - - - - */

  /** @brief fetch a memento for the current view configuration */
  viewMemento(): ViewMemento {

    if (this._currentFocus == null) {

      return null;
    }

    /*
     * we're including enough info that clients can understand what the project bound is, *and* what part of the
     * graph is actually being seen on screen.  This should be helpful for auxiliary views like x-axis/y-axis
     * widgets.
     *
     */

    const [sx0, sy0]          = this.worldToScreen(this._currentFocus.min.x, this._currentFocus.min.y);
    const [sx1, sy1]          = this.worldToScreen(this._currentFocus.max.x, this._currentFocus.max.y);

    const screen              = new Box2(new Vector2(sx0, sy0), new Vector2(sx1, sy1));

    const [gx0, gy0]          = this.worldToScreen(this._bound.min.x, this._bound.min.y);
    const [gx1, gy1]          = this.worldToScreen(this._bound.max.x, this._bound.max.y);

    const graph              = new Box2(new Vector2(gx0, gy0), new Vector2(gx1, gy1));

    const memento: ViewMemento = new ViewMemento(
      this._currentFocus,
      screen,
      this._bound,
      graph,
      this._camera.position.clone(),
      this._timeScale,
      this._distanceScale);

    memento._graph          = this._bound;

    return memento;
  }

  /** @brief the selection should be redrawn, empty means no selection */
  setSearch(search: MovementHistory[]) {

    let tsv: ThreeStringViewer = this;

    /* clear the current selection layer */

    tsv._searchObject.remove(...tsv._searchObject.children);

    /* add a new selection line for each selected history */

    const    cGreen: Color  = new Color('#00FF00');
    const     cBlue: Color  = new Color('#1589ee');
    let   converter: Color  = cGreen;
    let   thickness: number = tsv._hoverPad;
    let     opacity: number = 0.7;

    for (const history of search) {

      /* if a train search result is in focus, its blue, otherwise green */

      if (tsv._searchManager.isFocus(history)) {

        converter = cBlue;

      } else {

        converter = cGreen;
      }

      /* build the line for this train */

      let lineMesh: Mesh = (new MeshLineBuilder())
        .setColor(converter)
        .setThickness(thickness)
        .setHistory(history)
        .setOpacity(opacity)
        .build(tsv._width, tsv._height);

      lineMesh.geometry.name = 'search_' + history._train._id;

      tsv._searchObject.add(lineMesh);

    } /* end of movement history walk */

  }

  /** @brief the selection should be redrawn, empty means no selection */
  setSelection(selected: MovementHistory[]) {

    let tsv: ThreeStringViewer = this;

    /* clear the current selection layer */

    tsv._selectObject.remove(...tsv._selectObject.children);

    /* add a new selection line for each selected history */

    const   cYellow: Color  = new Color('#FFFF00');
    const     cBlue: Color  = new Color('#1589ee');
    let   converter: Color  = cYellow;
    let   thickness: number = tsv._hoverPad;
    let     opacity: number = 0.7;

    for (const history of selected) {

      /* if a selected train is in focus, color it blue, otherwise the selection color (yellow) */

      if (this._selectionManager.isFocus(history)) {

        converter = cBlue;

      } else {

        converter = cYellow;
      }

      /* build the line for this train */

      let lineMesh: Mesh = (new MeshLineBuilder())
        .setColor(converter)
        .setThickness(thickness)
        .setHistory(history)
        .setOpacity(opacity)
        .build(tsv._width, tsv._height);

      lineMesh.geometry.name = 'selection_' + history._train._id;

      tsv._selectObject.add(lineMesh);

    } /* end of movement history walk */

  }

  /** @brief pass keyboard focus to the main view */
  takeFocus() {

    $(this._drawingSurface).focus();

  }

  /** @brief scale the time axis by this factor */
  scaleTimeTo(scale: number) {

    if (Number.isNaN(scale)) {

      Logger.warning(`[threeview] scaleTimeTo - bad scale value (not a number).`);

      scale = 1.0;
    }

    if (this._drawingSurface == null) {

      /* nothing to do yet */

      return ;
    }

    if (scale < 1.0) {

      scale = 1.0;
    }

    if (scale > 1000) {

      scale = 1000;
    }

    /* rescale geometry */

    this._timeScale = scale;

    this.scaleUpdate();

  }

  /** @brief scale the distance axis by this factor */
  scaleDistanceTo(scale: number) {

    if (Number.isNaN(scale)) {

      Logger.warning(`[threeview] scaleDistanceTo - bad scale value (not a number).`);

      scale = 1.0;
    }

    if (this._drawingSurface == null) {

      /* nothing to do yet */

      return ;
    }

    if (scale < 0.5) {

      scale = 0.5;
    }

    if (scale > 100) {

      scale = 100;
      scale = 100;
    }

    /* rescale geometry */

    this._distanceScale = scale;

    this.scaleUpdate();
  }

  /**
   * amount in world space to pan horizontally; this is essentially what the time axis widget
   * uses to tweak the graph when the graph is scaled or panned via the time axis.
   *
   */
  panX(delta: number) {

    this._camera.position.set(
      this._camera.position.x + delta,
      this._camera.position.y,
      this._camera.position.z
    );

    this._camera.updateProjectionMatrix();
    this._camera.updateMatrix();
    this._camera.updateMatrixWorld(true);

    this._hoverObject.remove(...this._hoverObject.children);

    this._xAxis.update();

  }

  /**
   * amount in world space to pan vertically; this is essentially what the distance axis widget
   * uses to tweak the graph when the graph is scaled or panned via the time axis.
   *
   */
  panY(delta: number) {

    this._camera.position.set(
      this._camera.position.x,
      this._camera.position.y + delta,
      this._camera.position.z
    );

    this._camera.updateProjectionMatrix();
    this._camera.updateMatrix();
    this._camera.updateMatrixWorld(true);

    this._hoverObject.remove(...this._hoverObject.children);

    this._yAxis.update();
  }

  /** @brief helper to adjust everything after we update the scale of X or Y */
  scaleUpdate() {

    $(`#xscale-picker_${this.uuid}`).val(this._timeScale.toFixed(4));
    $(`#yscale-picker_${this.uuid}`).val(this._distanceScale.toFixed(4));

    this._clippingPlane.constant = this._clipX * this._timeScale;

    this.updateBound();

    this.refreshVisuals();

  }

  /** @brief zoom/pan as needed to return the viewer to 'here' */
  focus(here: ViewMemento): void {

    if (this.viewLocked) {

      Logger.debug('[threeview] focus, view is locked, skipping refocus');

      return ;
    }

    Logger.debug('[threeview] focus (here): x: ', here._camera.x, ' y:', here._camera.y, ' z:', here._camera.z);

    this.scaleDistanceTo(here._distanceScale);
    this.scaleTimeTo(here._timeScale);

    this._camera.position.set(here._camera.x, here._camera.y, here._camera.z);

    this.refreshVisuals();
  }

  /** re-initialize with whatever the current settings are. */
  public refresh() {

    Logger.debug('[setTimezone] refreshing ...');

    this.loadError = '';

    if (this._timezone == 'UTC') {

      /* no timezone conversion */

      this.setScenario(this._utcScenario);

      return ;
    }

    /* we ask the model to give us a timezone adjusted version of itself */

    this.lock("Refreshing...");

    setTimeout( () => {

      let adjusted: Scenario = this._utcScenario.timezone(this._timezone);

      this.setScenario(adjusted, this._utcScenario);

      this.unlock();

    }, 500);

  }

  /** ensure the current view frustum bound is up-to-date. */
  public updateFocus(): void {

    let tsv: ThreeStringViewer = this;

    const [wx0, wy0] = tsv.screenToWorld(0, 0);
    const [wx1, wy1] = tsv.screenToWorld(tsv._width, tsv._height);

    tsv._currentFocus = new Box2(new Vector2(wx0, wy0), new Vector2(wx1, wy1));

  }

  /** @brief helper to make sure everything is in sync with latest zoom/pan/axis scale */
  public refreshVisuals(): void {

    let tsv: ThreeStringViewer = this;

    tsv.loadError = '';

    /* make sure camera is up-to-date with current ratio of screen to world */

    tsv._camera.updateProjectionMatrix();
    tsv._camera.updateMatrix();
    tsv._camera.updateMatrixWorld(true);

    /* make sure the focus bound is up-to-date with our current pan position */

    tsv.updateFocus();

    /* redraw selection if needed */

    tsv.setSelection(tsv._selectionManager.selection());

    /* redraw search if needed */

    tsv.setSearch(tsv._searchManager.search());

    /* are they hovering over a train movement? */

    tsv.highlightCheck();

    /* make sure the time and distance scales are aligned with our position */

    tsv._yAxis.setScenario(tsv._scenario);
    tsv._xAxis.setScenario(tsv._scenario);

    tsv._yAxis.update();
    tsv._xAxis.update();

    /* redraw any extra annotations. */

    tsv.drawExtras();
  }

  /* - - - - - - local methods - - - - - - - - */

  /** @brief reset the view to empty */
  public clear() {

    /* $('#train-table-container').html('<p>Please select a scenario (JSON file) to begin.</p>'); */

    this._scenario = null;

  }

  public close() {

    this.destroy();
  }

  /** @brief standard destructor */
  destroy() {

    Logger.debug('[threeview]', 'cleaning up...');

    this._noAnimate          = true;

    this._yAxis              = null;
    this._xAxis              = null;
    this._renderer           = null;
    this._camera             = null;
    this._scenario           = null;
    this._scene              = null;
    this._trainLines         = null;
    this._crossings          = null;
    this._gridLines24        = null;
    this._gridCurrent        = null;
    this._userLogBounds      = null;
    this._workLines          = null;
    this._selectionManager   = null;
    this._searchManager      = null;
    this._parkabilityManager = null;
    this._authorityManager   = null;

    if (this._limitSub != null) {

      this._limitSub.unsubscribe();
      this._limitSub = null;
    }

  }

  /** the pan/zoom handle will give us mouse, keyboard as well as pan/zoom events */
  private handleEvent(event: ViewerEvent): void {

    //Logger.debug(`[threeview] pan/zoomevent: `, event);

    if (event.type == ViewerEventType.PAN ||
        event.type == ViewerEventType.PAN_END ||
        event.type == ViewerEventType.ZOOM_END) {

      /*
       * the pan/zoom control already moved the camera in some way and updated the projector
       * but we still have to do a few things:
       *
       *   - remove any highlight graph, since It's still at the old (invalid position)
       *
       *   - tell the axis widgets to redraw, since we're at a new position now.
       *
       */

      this._hoverObject.remove(...this._hoverObject.children);

      this._xAxis.update();

      this._yAxis.update();

    }

    /*
     * we have our own event watching for keyboard bindings and context menu, so just ignore
     * the even stream from the PanZoom pseudo camera.  If we let it bubble up...it can interfere
     * with other places where we have direct event handling, like the devbridge-autocomplete
     * dropdowns.
     *
     */

    if (event.getOriginalEvent()) {

      event.getOriginalEvent().preventDefault();
    }
  }

  /** @brief after setting a scenario, we must reset everything and render a fresh scene */
  init(): void {

    Logger.debug(`[threeview] init - starts...`);

    const tsv: ThreeStringViewer = this;

    tsv.loadError = '';

    /* create the Three.js rendering pipeline */

    tsv._drawingSurface    = document.getElementById( `three-string-viewer-canvas_${tsv.uuid}`) as HTMLCanvasElement;

    tsv._renderer          = new WebGLRenderer( {
      antialias: true,
      canvas: tsv._drawingSurface,
    });

    /*
     * clipping in Three.js:
     *
     *   https://jsfiddle.net/oe45ptsd/
     *   https://stackoverflow.com/questions/41946069/dynamic-clipping-or-csg-operations-in-three-js
     *   https://github.com/mrdoob/three.js/blob/master/examples/webgl_clipping.html
     *   https://discourse.threejs.org/t/planehelper-has-a-different-clipping-location-then-object-in-the-scene/18008
     *
     */

    tsv._renderer.localClippingEnabled = true;

    tsv._width             = tsv._drawingSurface.getBoundingClientRect().width;
    tsv._height            = tsv._drawingSurface.getBoundingClientRect().height;

    $(tsv._drawingSurface).attr('tabindex', '0');
    $(tsv._drawingSurface).attr('contentEditable', 'true');
    $(tsv._drawingSurface)[0].contentEditable = 'true';

    $(`#three-string-grid-canvas_${tsv.uuid}`).width(this._width);
    $(`#three-string-grid-canvas_${tsv.uuid}`).height(this._height);

    const grid: HTMLCanvasElement = document.getElementById(`three-string-grid-canvas_${tsv.uuid}`) as HTMLCanvasElement;
    grid.width                    = this._width;
    grid.height                   = this._height;

    tsv._renderer.setPixelRatio( window.devicePixelRatio );
    tsv._renderer.setSize(this._width, this._height );

    tsv._camera = new PerspectiveCamera( 70, tsv._width / tsv._height, tsv._near, tsv._far );
    tsv._camera.position.z    = 1;

    tsv._timeScale            = 1.0;
    tsv._distanceScale        = 1.0;

    $(`#xscale-picker_${tsv.uuid}`).val(1.0);
    $(`#yscale-picker_${tsv.uuid}`).val(1.0);

    /* setup the static scene in Three.js (on the GPU) */

    tsv._scene                = new Scene();

    if (tsv._nightMode) {

      tsv._scene.background   = new Color(tsv._prefs.night.background);

    } else {

      tsv._scene.background   = new Color(tsv._prefs.day.background);
    }

    Logger.debug(`[threeview] init - building scene...`);

    tsv.initScene();

    /*
     * do an initial zoom that has a square bound with the height being the outer size.
     * We're going to vertically fit, so we want the height to "just fit".
     *
     */

    Logger.debug(`[threeview] init - configuring...`);

    tsv._currentFocus = tsv._bound;

    /* set the new axis */

    tsv._yAxis = new DistanceAxis(`three-string-yaxis-canvas_${tsv.uuid}`, tsv);
    tsv._xAxis = new TimeAxis(`three-string-xaxis-canvas_${tsv.uuid}`, `three-string-timecursor-canvas_${tsv.uuid}`, tsv);

    tsv._yAxis.setNightMode(tsv._nightMode);
    tsv._xAxis.setNightMode(tsv._nightMode);

    tsv._parkabilityManager.setScenario(tsv._scenario);
    tsv._authorityManager.setScenario(tsv._scenario);

    /* set the initial pan/zoom to auto-fit the graph */

    tsv._panZoom.setBound(tsv._bound);

    Logger.debug(`[threeview] init - refreshing visuals...`);

    tsv.refreshVisuals();

    Logger.debug(`[threeview] init - auto-fitting...`);

    tsv.onAutoFitToggle();

    /*
     * if we have a default zoom/position for string line viewers, then refocus the view there.  THe use
     * case is that they are viewing SIMILAR simulations and just want to stay focused on where they are
     * in the graph...across these similar simulations.
     *
     */

    let defaultView: ViewMemento|null = this.ide.defaultStringLineView;

    if (defaultView) {

      Logger.debug(`[threeview] init - using default view: `, defaultView);

      this.focus(defaultView);

    }

    /* if we load a new scenario, we have to start with no selection/search matches */

    tsv._selectObject.remove(...tsv._selectObject.children);
    tsv._searchObject.remove(...tsv._searchObject.children);
    tsv._hoverObject.remove(...tsv._hoverObject.children);
    tsv._authoritiesObject.remove(...tsv._authoritiesObject.children);
    tsv._authoritiesObjectHover.remove(...tsv._authoritiesObjectHover.children);
    tsv._parkabilityObject.remove(...tsv._parkabilityObject.children);

    $(`#timescale-picker_${tsv.uuid}`).val($(`#timescale-picker_${tsv.uuid} option:first`).val());
    tsv._xAxis.setScale('auto');

    /*
     * subscribe to the limit cursor events, so we know when the right side limit
     * has moved or its mode has been toggled.
     *
     */

    tsv._limitSub = tsv._xAxis.limit().subscribe((cursor: TimeCursor) => {

      if (cursor === null) {

        return;
      }

      tsv.limitCursorUpdate(cursor);

    });

    Logger.debug(`[threeview] init - completed OK!`);

  }

  /** fetch the current extend of the graph including scaling */
  public getScaledBound(): Box2 {

    let tsv: ThreeStringViewer = this;

    return new Box2(

      new Vector2(
        tsv._dataX * tsv._timeScale,
        tsv._dataY * tsv._distanceScale),

      new Vector2(
        tsv._dataWidth  * tsv._timeScale,
        tsv._dataHeight * tsv._distanceScale)

    );
  }

  /** sync our bounding box */
  private updateBound(): void {

    let tsv: ThreeStringViewer = this;

    if (!tsv._scene) {

      /* there is no scene yet */

      return ;
    }

    tsv._bound         = tsv.getScaledBound();

    tsv._scene.scale.x = tsv._timeScale;
    tsv._scene.scale.y = tsv._distanceScale;

    tsv._panZoom?.setBound(tsv._bound);

    //Logger.debug(`[threeview] updateBound - new bound:`, tsv._bound);
  }

  autoFit() {

    let tsv: ThreeStringViewer = this;

    if (!tsv._panZoom) {

      /* not yet initialized enough */

      return ;
    }

    tsv.verticalFit();

    /* refresh the view */

    tsv.refreshVisuals();
  }

  /**
   * fit the graph so that it vertically fills the screen, but then let the
   * horizontal scale be whatever it needs to be (scroll off the right side).
   *
   */
  public verticalFit(): void {

    let tsv: ThreeStringViewer = this;

    if (!tsv._panZoom || !tsv._camera) {

      /* no scene yet */

      return ;
    }

    /* we set a reasonable default for scaling */

    let scaleX: number = 15.0;

    /* build a bounding box with the scaling (whatever the current scaling is) */

    let {x: dataX, y: dataY}          = tsv._bound.min;
    let {x: dataWidth, y: dataHeight} = tsv._bound.max;

    dataX      /= tsv._timeScale;
    dataY      /= tsv._distanceScale;
    dataWidth  /= tsv._timeScale;
    dataHeight /= tsv._distanceScale;

    let unitBox = new Box2(
      new Vector2(dataX, dataY),
      new Vector2(dataX + dataHeight - dataY, dataHeight)
    );

    tsv._currentFocus = unitBox;

    /*
     * fit it vertically so that we know for sure the distance range is all on screen.
     * the simplest way to do that is zoom on a square that has all sides being the height.
     * this will center the square in the camera, and has no relation to the actual width
     * of the graph...but it's fine, because below we allow the distance scale to be whatever
     * it needs to be, and we translate the graph so that "0" is in the lower left corner
     * no matter what.
     *
     * that would be all that is needed, except that the "screen" has overlays of the x and
     * y-axis as well as the action bar at the top.  So ensure we fit (vertically) the graph
     * to the space not already consumed by the x-axis and the action bar at the top...we
     * have to scale as if we were projecting for that smaller screen.
     *
     */

    tsv._panZoom.zoomToBox(unitBox);

    tsv._currentFocus = tsv._panZoom.worldViewBound();

    /*
     * when we auto-fit the graph, let the zoom control know tha the camera position is
     * new "max" camera position to zoom out to, for a presumed full view.
     *
     */

    tsv._panZoom.setFullZoomOut(tsv._camera.position.z);

    /*
     * the vertical scale is just 1, so to "shrink" it to fit between the action bar at the top
     * and x-axis at teh bottom, we project the full screen height, then the height minus those
     * parts, and use the ration as our distance scaling.  It will only work at the current
     * zoom level, but it only has to work at this zoom level.
     *
     */

    let [, oy] = tsv.screenToWorld(0, 0);
    let [, ay] = tsv.screenToWorld(0, tsv._height);
    let [, by] = tsv.screenToWorld(0, tsv._height - 42 - tsv._xAxis.height());

    let shrinkage: number = (by - oy) / (ay - oy);

    /* shrink vertically so we fit inside the middle area */

    tsv.scaleDistanceTo(shrinkage);

    /* let the time axis be whatever our default scale out is. distance axis is just 1.0 for now. */

    tsv.scaleTimeTo(scaleX);

    {
      /*
       * drag the start of the graph horizontally so that its on the left side of the canvas, but
       * to the right of the distance axis
       *
       */

      let {wx: w1} = tsv._panZoom.screenToWorld(tsv._yAxis.width(), 0);
      let w2: number = tsv._dataX * tsv._timeScale;

      tsv._camera.position.x += (w2 - w1);

      tsv.refreshVisuals();
    }

    {
      /*
       * drag the start of the graph vertically so that its on the bottom side of the canvas, but above
       * the timescale (leave a little extra space so that "0.0" isn't half clipped.
       *
       */

      let {wy: w1} = tsv._panZoom.screenToWorld(0, tsv._yAxis.height() - 10);
      let w2: number = tsv._dataY * tsv._distanceScale;

      tsv._camera.position.y += (w2 - w1);

      tsv.refreshVisuals();
    }

  }

  /** @brief when the limit cursor moves or toggles mode, we have to potentially update clipping render additionals */
  limitCursorUpdate(cursor: TimeCursor) {

    if (cursor == null) {

      this._clipX = this._dataWidth;
      this._clippingPlane.constant = ( this._clipX ) * this._timeScale;

      return ;
    }

    if (cursor.mode() == TimeCursorMode.OFF) {

      /* make sure we aren't clipping */

      this._clipX = this._dataWidth;
      this._clippingPlane.constant = ( this._clipX ) * this._timeScale;

      this._limitMode = cursor.mode();

      /* don't display any extras */

      this._authoritiesObject.visible      = false;
      this._authoritiesObjectHover.visible = false;
      this._parkabilityObject.visible      = false;

      return ;
    }

    /* if we are already at this time location and already showing this mode, nothing to do */

    if ((cursor._wt == this._authoritiesWT) && (cursor.mode() == this._limitMode)) {

      /* already set */

      return ;
    }

    Logger.debug(`[threeview] new limit cursor: `, cursor);

    /* update limit clipping */

    this._clipX                  = cursor._wt;

    this._clippingPlane.constant = ( this._clipX ) * this._timeScale;

    /* depending on which mode we are in, we can now show extra to the right of the clipping line */

    this._limitMode = cursor.mode();

    switch(this._limitMode) {

      case TimeCursorMode.OFF: {

        /* just hide the extras */

        this._authoritiesObject.visible      = false;
        this._authoritiesObjectHover.visible = false;
        this._parkabilityObject.visible      = false;

        this._clippingPlane.constant = ( this._dataWidth ) * this._timeScale;

        this.setSelection(this._selectionManager.selection());

        this.setSearch(this._searchManager.search());

        this.highlightCheck();
      }
      break;

      case TimeCursorMode.AUTHORITIES: {

        /* hide !authorities */

        this._parkabilityObject.visible = false;

        /* show authorities */

        this.showAuthorities(cursor._wt);
      }
      break;

      case TimeCursorMode.PARKABILITY: {

        /* hide !parkability */

        this._authoritiesObject.visible      = false;
        this._authoritiesObjectHover.visible = false;

        /* show parkability */

        this.showParkability(cursor._wt);
      }
      break;

    }


  }

  /** @brief animation "frame", we are *always* animating */
  animate(): void {

    if (this._renderer === null) {

      return ;
    }

    if (this._noAnimate) {

      return ;
    }

    const canvas: HTMLCanvasElement = document.getElementById(`three-string-grid-canvas_${this.uuid}`) as HTMLCanvasElement;

    if (canvas === null) {

      /* the viewer has been closed! */

      return ;
    }

    /* queue up another one to happen again later */

    requestAnimationFrame(() => this.animate());

    /* draw the HUD */

    this.drawHUD();

    /* (Three.js) render */

    this._renderer.render( this._scene, this._camera );
  }

  /** refresh the mesh for authorities or parkability if we are showing either one */
  drawExtras(loading: boolean = false): void {

    if (this._limitMode == TimeCursorMode.AUTHORITIES) {

      this.drawAuthorities(loading);

    } else if  (this._limitMode == TimeCursorMode.PARKABILITY) {

      this.drawParkability(loading);
    }

  }

  /** (re)draw the authorities blocks */
  drawAuthorities(loading: boolean = false): void {

    this._authoritiesObject.remove(...this._authoritiesObject.children);
    this._authoritiesObjectHover.remove(...this._authoritiesObjectHover.children);

    this._authorityManager.generate(this._authoritiesWT, this._authoritiesThickness, this._authoritiesObject, this._authoritiesObjectHover);

  }

  /** (re)draw the parkability blocks */
  drawParkability(loading: boolean = false): void {

    this._parkabilityObject.remove(...this._parkabilityObject.children);

    this._parkabilityMesh = this._parkabilityManager.generate(this._parkabilityWT, this._parkabilityThickness);

    if (this._parkabilityMesh.length > 0) {

      this._parkabilityObject.add(...this._parkabilityMesh);
    }

  }

  drawHUD(): void {

    /* clear the overlay canvas */

    const canvas: HTMLCanvasElement = document.getElementById(`three-string-grid-canvas_${this.uuid}`) as HTMLCanvasElement;
    const context                   = canvas.getContext('2d');
    const oldAlpha                  = context.globalAlpha;

    context.clearRect(0, 0, this._width, this._height);

    if ((this._spikeLines) && (this._drawingSurface != null)) {

      /* redraw the spike lines */

      context.setLineDash([12, 4]);
      context.lineDashOffset = -this._spikeOffset;

      if (this._nightMode) {

        context.globalAlpha  = this._prefs.night.spike.alpha;
        context.strokeStyle  = this._prefs.night.spike.color;
        context.lineWidth    = this._prefs.night.spike.width;

      } else {

        context.globalAlpha  = this._prefs.day.spike.alpha;
        context.strokeStyle  = this._prefs.day.spike.color;
        context.lineWidth    = this._prefs.day.spike.width;
      }

      context.beginPath();
      context.moveTo(0, this._mouseCanvasY);
      context.lineTo(this._width, this._mouseCanvasY);
      context.stroke();


      context.beginPath();
      context.moveTo(this._mouseCanvasX, 0);
      context.lineTo(this._mouseCanvasX, this._height);
      context.stroke();

      /* restore transparency settings */

      context.globalAlpha = oldAlpha;
    }

    /* are we hovered over a parkability block? */

    const parkingHover: {slot: InterLinkPark, allowed: boolean} = this._parkabilityManager.hover();

    if (parkingHover != null) {

      /* where we can start drawing text */

      const leftMargin     = this._parkabilityThickness + 8;
      const topMargin      = 0;
      const rowSpacing     = 4;
      const left           = this._mouseCanvasX + leftMargin;
      const top            = this._mouseCanvasY + topMargin;

      const     fs: number = this._prefs.hud.fontSize;

      context.font         = 'normal bold ' + fs + 'px Verdana';
      context.textBaseline = 'bottom';
      context.textAlign    = 'left';

      const color          = parkingHover.allowed ? 'rgba(0, 255, 0, 1)' : 'rgba(255, 0, 0, 1)';

      let height         = 0;
      let width          = 0;

      const labels: {text: string, width: number, height: number}[] = [];
      const slots: {train: Train, track: Track, reason: string}[] =
        parkingHover.allowed ? parkingHover.slot.allowed : parkingHover.slot.denied;


      /* header of the info is the interlink name */

      if (slots.length > 0) {

        const  label: string = `${parkingHover.slot.link._name}`;

        const labelW: number = context.measureText(label).width;
        const labelH: number = fs + rowSpacing;

        height += labelH + rowSpacing;

        labels.push({text: label, width: labelW, height: labelH})
      }

      /* create a label for each train/track */

      slots.forEach((item: {train: Train, track: Track, reason: string}, i: number) => {

        const     train: Train  = item.train;
        const       dir: string = train._tc._dir === TrainDirection.EAST ? 'E' : 'W';
        const        id: string = train._name;
        let     schType: string = '';

        switch (train._type) {
          case TrainType.ACTUAL:      schType = 'A'; break;
          case TrainType.SCHEDULED:   schType = 'S'; break;
          case TrainType.WEEKLY:      schType = 'W'; break;
          case TrainType.EXPONENTIAL: schType = 'R'; break;
          case TrainType.EVEN:        schType = 'E'; break;
        }

        const  label: string = `${id} ${dir} ${Track.typeToString(item.track._type)}${item.track._track} - ${item.reason}`;

        const labelW: number = context.measureText(label).width;
        const labelH: number = fs + rowSpacing;

        height += labelH + rowSpacing;

        labels.push({text: label, width: labelW, height: labelH})
      });

      /* draw a background for entire bound of the labels */

      /* draw background (rectangle) for the text */

      context.beginPath();

      if (this._nightMode) {

        context.fillStyle = 'rgba(0, 0, 0, .4)';

      } else {

        context.fillStyle = 'rgba(255, 255, 255, .4)';
      }

      context.rect(left, top, width + rowSpacing, height);
      context.fill();

      /* draw the labels (row by row) */

      context.beginPath();

      let y: number = top;

      labels.forEach((item: {text: string, width: number, height: number}) => {

        context.fillStyle   = color;
        context.globalAlpha = 1.0;

        context.fillText(item.text, left, y);

        y += item.height;

      });

    }

    /* are we hovered over a parkability block? */

    const authorityHover: {slot: InterLinkAuth, east: boolean} = this._authorityManager.hover();

    if (authorityHover != null) {

      /* where we can start drawing text */

      const leftMargin     = this._authoritiesThickness + 8;
      const topMargin      = 0;
      const rowSpacing     = 4;
      const left           = this._mouseCanvasX + leftMargin;
      const top            = this._mouseCanvasY + topMargin;

      const     fs: number = this._prefs.hud.fontSize;

      context.font         = 'normal bold ' + fs + 'px Verdana';
      context.textBaseline = 'bottom';
      context.textAlign    = 'left';

      const color          = !authorityHover.east ? 'blue' : 'yellow';

      let height         = 0;
      let width          = 0;

      const labels: {text: string, width: number, height: number}[] = [];
      const slots: {train: Train, track: Track, reason: string}[] =
        authorityHover.east ? authorityHover.slot.east : authorityHover.slot.west;
      const dir: string = authorityHover.east ? "East Bound" : "West Bound"

      /* header of the info is the interlink name */

      if (slots.length > 0) {

        const  label: string = `${authorityHover.slot.link._name} ${dir}`;

        const labelW: number = context.measureText(label).width;
        const labelH: number = fs + rowSpacing;

        height += labelH + rowSpacing;

        labels.push({text: label, width: labelW, height: labelH})
      }

      /* create a label for each train/track */

      slots.forEach((item: {train: Train, track: Track, reason: string}, i: number) => {

        const     train: Train  = item.train;
        const       dir: string = train._tc._dir === TrainDirection.EAST ? 'E' : 'W';
        const        id: string = train._name;
        let     schType: string = '';

        switch (train._type) {
          case TrainType.ACTUAL:      schType = 'A'; break;
          case TrainType.SCHEDULED:   schType = 'S'; break;
          case TrainType.WEEKLY:      schType = 'W'; break;
          case TrainType.EXPONENTIAL: schType = 'R'; break;
          case TrainType.EVEN:        schType = 'E'; break;
        }

        const  label: string = `${id} ${dir} ${Track.typeToString(item.track._type)}${item.track._track}`;

        const labelW: number = context.measureText(label).width;
        const labelH: number = fs + rowSpacing;

        height += labelH + rowSpacing;

        labels.push({text: label, width: labelW, height: labelH})
      });

      /* draw a background for entire bound of the labels */

      /* draw background (rectangle) for the text */

      context.beginPath();

      if (this._nightMode) {

        context.fillStyle = 'rgba(0, 0, 0, .4)';

      } else {

        context.fillStyle = 'rgba(255, 255, 255, .4)';
      }

      context.rect(left, top, width + rowSpacing, height);
      context.fill();

      /* draw the labels (row by row) */

      context.beginPath();

      let y: number = top;

      labels.forEach((item: {text: string, width: number, height: number}) => {

        context.fillStyle   = color;
        context.globalAlpha = 1.0;

        context.fillText(item.text, left, y);

        y += item.height;

      });

    }

    /* are we hovered over a decision point? */

    if ((this._hoverDecisionPoint != null) && (this._hoverDecisionPoint.dp != null) && (this._drawingSurface != null)) {

      const leftMargin: number = 8;
      const topMargin: number  = 8;
      const rowSpacing: number = 4;
      const left: number       = this._mouseCanvasX + leftMargin;
      const top: number        = this._mouseCanvasY + topMargin;
      let height: number       = 0;
      let width: number        = 0;

      const     fs: number     = this._prefs.hud.fontSize;

      context.font             = 'normal bold ' + fs + 'px Verdana';
      context.textBaseline     = 'top';
      context.textAlign        = 'left';

      let workStatus: string   = '';

      const [cw, hw]           = this._scenario.workFlags(this._hoverDecisionPoint.dp, this._hoverDecisionPoint.train);

      if (cw) {

        workStatus += `CMT `;
      }

      if (hw) {

        workStatus += `HLD  `;
      }

      const       sStr: string = this.switchLayout(this._hoverDecisionPoint.dp, null, null);
      const      value: number = this._hoverDecisionPoint.dp._mileage;
      const eastBundle: string = (this._hoverDecisionPoint.dp._eastSide == null) ? '' : ' [' + InterLink.typeToString( this._hoverDecisionPoint.dp._eastSide._type) + ']';
      let        label: string = `${workStatus}${this._hoverDecisionPoint.dp._name.trim()} ${eastBundle} ${sStr.trim()} ${value.toFixed(1)} mi (${Train.iid(this._hoverDecisionPoint.train)})`;

      const labelW: number     = context.measureText(label).width;
      const labelH: number     = fs + rowSpacing;

      height += labelH + rowSpacing;
      width  += labelW;

      /* draw background (rectangle) for the text */

      context.beginPath();

      if (this._nightMode) {

        context.fillStyle = 'rgba(0, 0, 0, .4)';

      } else {

        context.fillStyle = 'rgba(255, 255, 255, .4)';
      }

      context.rect(left, top, width + rowSpacing, height);
      context.fill();

      /* draw the text */

      context.beginPath();

      if (this._nightMode) {

        context.fillStyle = 'rgba(255, 255, 255, 1)';

      } else {

        context.fillStyle = 'rgba(0, 0, 0, 1)';
      }

      context.globalAlpha = 1.0;

      context.fillText(label, left, top + 4);
    }

    /* are we hovered over a train movement line? */

    if ((this._hoverMove != null) && (this._drawingSurface != null) && (this._hoverDecisionPoint == null)) {

      const  cx: number = this._mouseCanvasX;
      const  cy: number = this._mouseCanvasY;

      /* padding from cross-hairs to start of text */

      const cpx: number = this._prefs.hud.centerPadX;
      const cpy: number = this._prefs.hud.centerPadY;

      /** @note canvas co-ords have an inverted Y axis (0 at top max at bottom) */

      /*
       * a mini-heads-up-display:
       *
       *                    |
       *     Train info (0) X  Plant info (1)
       *     --------------XXX---------------
       *     Train Time (2) X  Dwell Time (3)
       *                    |
       *
       * Font styling:
       *
       *   @link http://tutorials.jenkov.com/html5-canvas/text.html
       *
       */

      /* the text we want in each corner of the heads-up display */

      /*
       * train info
       *
       *   train direction
       *   train id
       *   train length
       *   speed class
       *   schedule type
       *     A = Actual
       *     S = Scheduled
       *     W = Weekly
       *     R = random exponential
       *     E = Even
       *   internal #
       *
       */

      {
        const     fs: number = this._prefs.hud.fontSize;

        context.font         = 'normal bold ' + fs + 'px Verdana';
        context.textBaseline = 'bottom';
        context.textAlign    = 'right';

        const   upArrow: string = '\u2191';
        const downArrow: string = '\u2193';
        const     train: Train  = this._hoverMove._parent._train;
        const       dir: string = train._tc._dir === TrainDirection.EAST ? downArrow : upArrow;
        const        id: string = train._name;

        const    length: string = Math.round(this._hoverMove.length()).toString() + ' ft';

        const  spdClass: string = this._hoverMove.trainClass()._name;
        const     intId: string = ' #' + train._id + ' Gen: ' + train.generatedID();
        let     schType: string = '';

        switch (train._type) {
          case TrainType.ACTUAL:      schType = 'A'; break;
          case TrainType.SCHEDULED:   schType = 'S'; break;
          case TrainType.WEEKLY:      schType = 'W'; break;
          case TrainType.EXPONENTIAL: schType = 'R'; break;
          case TrainType.EVEN:        schType = 'E'; break;
        }

        let     label: string = dir + ' ' + id + ', ' + length + ', ' + spdClass + ' (' + train._tc._id + ') ' + schType + intId;
        let    labelW: number = context.measureText(label).width;

        if (this._simpleHUD) {

          label  = dir + ' ' + id;
          labelW = context.measureText(label).width;

        }

        /* draw background (rectangle) for the text */

        context.beginPath();

        if (this._nightMode) {

          context.fillStyle = 'rgba(0, 0, 0, .4)';

        } else {

          context.fillStyle = 'rgba(255, 255, 255, .4)';
        }

        context.rect(cx - cpx - labelW - 2 * 8, cy - cpy - fs - 2 * 8, labelW + 2 * 8, fs + 2 * 8);
        context.fill();

        /* draw the text */

        if (this._nightMode) {

          context.fillStyle = 'rgba(255, 255, 255, 1)';

        } else {

          context.fillStyle = 'rgba(0, 0, 0, 1)';
        }

        context.globalAlpha  = 1.0;

        context.fillText(label, cx -  cpx - 8, cy - cpy - 8);

      }

      /*
       * plant info
       *
       *   - track id
       *   - track length
       *   - clear length
       *   - milepost
       *
       */

      if (!this._simpleHUD) {

        const   trkId: string       = Track.typeToString(this._hoverMove._track._type) + this._hoverMove._track._origTrack;

        const  trkLen: string       = Math.round(this._hoverMove._track._useLength * 5280).toString()  + ' ft';
        const  clrLen: string       = Math.round(this._hoverMove._track._physLength * 5280).toString() + ' ft';

        const     dp: DecisionPoint = this._hoverMove._dp;
        const     mp: string        = 'MP ' + dp._mileage.toFixed(2).toString();

        const label: string         = trkId + ', ' + trkLen + ' (' + clrLen + '), ' + mp;

        const     labelW: number    = context.measureText(label).width;
        const         fs: number    = this._prefs.hud.fontSize;

        context.font         = 'normal bold ' + fs + 'px Verdana';
        context.textBaseline = 'bottom';
        context.textAlign    = 'left';

        /* draw background (rectangle) for the text */

        context.beginPath();

        if (this._nightMode) {

          context.fillStyle = 'rgba(0, 0, 0, .4)';

        } else {

          context.fillStyle = 'rgba(255, 255, 255, .4)';
        }

        context.rect(cx + cpx, cy - cpy - fs - 2 * 8, labelW + 2 * 8, fs + 2 * 8);
        context.fill();

        /* draw the text */

        if (this._nightMode) {

          context.fillStyle = 'rgba(255, 255, 255, 1)';

        } else {

          context.fillStyle = 'rgba(0, 0, 0, 1)';
        }

        context.globalAlpha  = 1.0;

        context.fillText(label, cx + cpx + 8, cy - cpy - 8);
      }

      /*
       * train time
       *
       *   - arrival time
       *   - departure time
       *
       */

      {

        const     todDep: string = new Date(this._hoverMove._departure * 1000).toISOString().substr(11, 8);
        const       dDep: string = Math.floor(this._hoverMove._departure / 86400).toString().padStart(2, '0');
        const      dText: string = 'DAY ' + dDep + ' ' + todDep;

        const     todArr: string = new Date(this._hoverMove._arrival * 1000).toISOString().substr(11, 8);
        const       dArr: string = Math.floor(this._hoverMove._arrival / 86400).toString().padStart(2, '0');
        const      aText: string = 'DAY ' + dArr + ' ' + todArr;

        let      label: string = aText + ',  ' + dText;

        if (this._simpleHUD) {

          label = dText;

        }

        const     labelW: number = context.measureText(label).width;
        const         fs: number = this._prefs.hud.fontSize;

        context.font         = 'normal bold ' + fs + 'px Verdana';
        context.textBaseline = 'top';
        context.textAlign    = 'right';

        /* draw background (rectangle) for the text */

        context.beginPath();

        if (this._nightMode) {

          context.fillStyle = 'rgba(0, 0, 0, .4)';

        } else {

          context.fillStyle = 'rgba(255, 255, 255, .4)';
        }

        context.rect(cx -  cpx - 2 * 8 - labelW, cy + cpy, labelW + 2 * 8, fs + 2 * 8);
        context.fill();

        /* draw the text */

        if (this._nightMode) {

          context.fillStyle = 'rgba(255, 255, 255, 1)';

        } else {

          context.fillStyle = 'rgba(0, 0, 0, 1)';
        }

        context.globalAlpha  = 1.0;

        context.fillText(label, cx - cpx - 8, cy + cpy + 8);

      }

      /*
       * dwell time
       *
       *   - scheduled dwell (hh:mm:ss),
       *     Sum total of all scheduled stop times, ie: no dispatch delays, no headways etc..
       *     Only times generated from the stop events.
       *
       *   - delay time (hh:mm:ss),
       *     Sum total of all dispatch delays.  There are some accounting problems to be fixed
       *     in C++ as I see sometimes 99 days as the time.
       *
       *   - total dwell (hh:mm:ss)
       *     Sum total of all delays OTHER than scheduled dwell described above.  There are
       *     some accounting problems here too.
       *
       */

      if (!this._simpleHUD) {

        const     dltSec: number = this._hoverMove._departure - this._hoverMove._arrival;
        const     schSec: number = this.schedulingDelay(this._hoverMove);
        let       dlySec: number = dltSec - schSec;

        if (dlySec < 0) {

          dlySec = 0;
        }

        const      delay: string = new Date(dlySec * 1000).toISOString().substr(11, 8);
        const  scheduled: string = new Date(schSec * 1000).toISOString().substr(11, 8);
        const      total: string = new Date(dltSec * 1000).toISOString().substr(11, 8);

        const      label: string = 'S ' + scheduled + ' D ' + delay + ' T ' + total;

        const     labelW: number = context.measureText(label).width;
        const         fs: number = this._prefs.hud.fontSize;

        context.font          = 'normal bold ' + fs + 'px Verdana';
        context.textBaseline  = 'top';
        context.textAlign     = 'left';

        /* draw background (rectangle) for the text */

        context.beginPath();

        if (this._nightMode) {

          context.fillStyle = 'rgba(0, 0, 0, .4)';

        } else {

          context.fillStyle = 'rgba(255, 255, 255, .4)';
        }

        context.rect(cx + cpx, cy + cpy, labelW + 2 * 8, fs + 2 * 8);
        context.fill();

        /* draw the text */

        if (this._nightMode) {

          context.fillStyle = 'rgba(255, 255, 255, 1)';

        } else {

          context.fillStyle = 'rgba(0, 0, 0, 1)';
        }

        context.globalAlpha  = 1.0;

        context.fillText(label, cx + cpx + 8, cy + cpy + 8);
      }

    }

  }

  /** @brief helper to sum up all the scheduling delay (stop events), in seconds, for a move */
  public schedulingDelay(move: Movement): number {

    let seconds: number = 0;

    for (const item of move._delays) {

      /* is this delay a scheduled delay? */

      if (MovementDelay.toDelayType(item._type) !== DelayType.DELAY_TYPE_SCHEDULED) {

        /* not interested */

        continue;
      }

      seconds += item._hours * 3600.0;
    }

    /* pass back the delays */

    return seconds;
  }

  /** @brief helper to pick line style based on the train */
  public trainStylePicker(train: Train): string {

    let color: string;

    if (this._nightMode) {

      color = this._prefs.night.trainColor.default;

    } else {

      color = this._prefs.day.trainColor.default;
    }

    /*
     *  Just show entire trace in color that indicates the kind of train ...
     *
     *    A-4XX     - Black/white
     *    M-3XX     - Black/white
     *    S/B/C/U/G - These are bulk trains, make them green
     *    P         - Passenger make it blue
     *    L/K/R     - Locals make them Grey
     *    Q - 1XX   - Intermodal is orange
     *
     */

    const char: string = train._name.trim().charAt(0).toUpperCase();

    if (this._nightMode) {

      switch (char) {

        case 'A':
          color = this._prefs.night.trainColor.trainA;
          break;

        case 'M':
          color = this._prefs.night.trainColor.trainM;
          break;

        case 'S':
          color = this._prefs.night.trainColor.trainS;
          break;

        case 'B':
          color = this._prefs.night.trainColor.trainB;
          break;

        case 'C':
          color = this._prefs.night.trainColor.trainC;
          break;

        case 'U':
          color = this._prefs.night.trainColor.trainU;
          break;

        case 'G':
          color = this._prefs.night.trainColor.trainG;
          break;

        case 'P':
          color = this._prefs.night.trainColor.trainP;
          break;

        case 'L':
          color = this._prefs.night.trainColor.trainL;
          break;

        case 'K':
          color = this._prefs.night.trainColor.trainK;
          break;

        case 'R':
          color = this._prefs.night.trainColor.trainR;
          break;

        case 'Q':
          color = this._prefs.night.trainColor.trainQ;
          break;

        case 'Z':
          color = this._prefs.night.trainColor.trainZ;
          break;

      }

    } else {

      switch (char) {

        case 'A':
          color = this._prefs.day.trainColor.trainA;
          break;

        case 'M':
          color = this._prefs.day.trainColor.trainM;
          break;

        case 'B':
          color = this._prefs.day.trainColor.trainB;
          break;

        case 'C':
          color = this._prefs.day.trainColor.trainC;
          break;

        case 'U':
          color = this._prefs.day.trainColor.trainU;
          break;

        case 'G':
          color = this._prefs.day.trainColor.trainG;
          break;

        case 'P':
          color = this._prefs.day.trainColor.trainP;
          break;

        case 'L':
          color = this._prefs.day.trainColor.trainL;
          break;

        case 'K':
          color = this._prefs.day.trainColor.trainK;
          break;

        case 'R':
          color = this._prefs.day.trainColor.trainR;
          break;

        case 'Q':
          color = this._prefs.day.trainColor.trainQ;
          break;

        case 'Z':
          color = this._prefs.day.trainColor.trainZ;
          break;

      }
    }

    return color;
  }

  /*SN-1410 Track colors*/
  public trackStylePicker(move:Movement): any {

    let   color: string;
    const track: Track         = move._track;
    const  type: InterLinkType = move._track._peers._type;

    if (track._type == TrackType.JUNCTION) {

      /* junctions are always pink */

      if (this._nightMode) {

        color = this._prefs.night.trackColor.junction;

      } else {

        color = this._prefs.day.trackColor.junction;
      }

    } else if (type == InterLinkType.TERMINAL) {

      /* terminal tracks are orange */

      if (this._nightMode) {

        color = this._prefs.night.trackColor.terminal;

      } else {

        color = this._prefs.day.trackColor.terminal;
      }

    } else {

      /* use hash map, based on combining track type character with track number for instance M1,M2 */

      let  color_picker:Array<string> = this._trackColorPrefs.get(Track.typeToString(track._type) + track._track);

      /* in case we haven't defined that color */

      if (!color_picker) {

        color_picker = this._trackColorPrefs.get("M1");

      }

      /*Select the right color if in night mode*/

      if (this._nightMode){

        color = color_picker[0];

      } else{

        color = color_picker[1];

      }

    }

    return color;
  }

  /** @brief helper to generate the switch layout summary string for a decision point */
  public switchLayout(dp: DecisionPoint, il: InterLink, t: Track): string {

    /* the junction layout */

    let sStr: string = '';

    switch (dp.switchLayout(t)) {

      case SwitchingLayout.AllCross: {

        sStr = 'X' + '\u2798' + '\u279a';

        break;
      }

      case SwitchingLayout.NoCross: {

        if (il == null) {

          sStr = 'X' + '\u2212';

        } else {

          if (il._type === InterLinkType.DOUBLE) {

            sStr = 'X' + '\u21c6';

          } else {

            sStr = 'X' + '\u2212';

          }
        }
        break;
      }

      case SwitchingLayout.StraightThrough: {

        if (il == null) {

          sStr = 'X' + '\u2212';

        } else {

          if (il._type === InterLinkType.DOUBLE) {

            sStr = 'X' + '\u21c6';

          } else {

            sStr = 'X' + '\u2212';

          }
        }
        break;
      }

      case SwitchingLayout.LeftHandCross: {

        sStr = 'X' + '\u2798';

        break;
      }

      case SwitchingLayout.RightHandCross: {

        sStr = 'X' + '\u279a';

        break;
      }
    }

    return sStr;
  }

  /** @brief push the geometry of the data graph to the GPU, when done we can start viewing */
  initScene(): void {

    let tsv: ThreeStringViewer = this;

    tsv.loadError = '';

    /* the train lines */

    {

      let trainLineBuilder: TrainLinesBuilder = new TrainLinesBuilder();

      tsv._trainLines = trainLineBuilder
        .setRenderer(tsv._renderer)
        .setScenario(tsv._scenario)
        .setDistanceScale(tsv._distanceScale)
        .setTimeScale(tsv._timeScale)
        .setClippingPlane(tsv._clippingPlane)
        .setColorMode(tsv._colorMode)
        .setTrackStylePicker((move: Movement) => { return tsv.trackStylePicker(move);})
        .setTrainStylePicker((train: Train) => { return tsv.trainStylePicker(train);})
        .build();

      tsv._dataX      = trainLineBuilder.getX();
      tsv._dataY      = trainLineBuilder.getY();
      tsv._dataWidth  = trainLineBuilder.getWidth();
      tsv._dataHeight = trainLineBuilder.getHeight();

      tsv._clipX      = tsv._dataWidth;

      if (tsv._trainLines) {

        tsv._scene.add(tsv._trainLines);

      } else {

        Logger.error(`[threeview] initScene - could not build train lines.`);
      }

    }

    /* the diamond crossings */

    {
      let crossingsBuilder: CrossingsBuilder = new CrossingsBuilder();

      tsv._crossings = crossingsBuilder
        .setRenderer(tsv._renderer)
        .setScenario(tsv._scenario)
        .setDistanceScale(tsv._distanceScale)
        .setTimeScale(tsv._timeScale)
        .setClippingPlane(tsv._clippingPlane)
        .build();

      if (tsv._crossings) {

        tsv._scene.add(tsv._crossings);

      } else {

        Logger.error(`[threeview] initScene - could not build crossings lines.`);
      }
    }

    /* the work blocks */

    if (tsv._scenario.workBlocks().size) {

      tsv._workLines = (new WorkBlocksBuilder())
        .setScenario(tsv._scenario)
        .setClippingPlane(tsv._clippingPlane)
        .setNightMode(tsv._nightMode)
        .setPrefs(tsv._prefs)
        .build();

      if (tsv._workLines) {

        tsv._scene.add(tsv._workLines);

      } else {

        Logger.error(`[threeview] initScene - could not build work blocks lines.`);
      }

    }

    /* the grid lines */

    {
      tsv._gridLines24 = (new GridBuilder())
        .setScenario(tsv._scenario)
        .setClippingPlane(tsv._clippingPlane)
        .setX(tsv._dataX)
        .setY(tsv._dataY)
        .setWidth(tsv._dataWidth)
        .setHeight(tsv._dataHeight)
        .setDistanceScale(tsv._distanceScale)
        .setTimeScale(tsv._timeScale)
        .setNightMode(tsv._nightMode)
        .build();

      if (tsv._gridLines24) {

        tsv._scene.add(tsv._gridLines24);

        tsv._gridCurrent = tsv._gridLines24;

      } else {

        Logger.error(`[threeview] initScene - could not build grid lines.`);
      }
    }

    /* extents for user logging */

    if (tsv._scenario.hasUserLogging()) {

      tsv._userLogBounds = (new UserLoggingBuilder())
        .setScenario(tsv._scenario)
        .setX(tsv._dataX)
        .setY(tsv._dataY)
        .setWidth(tsv._dataWidth)
        .setHeight(tsv._dataHeight)
        .setNightMode(tsv._nightMode)
        .build();

      if (tsv._userLogBounds) {

        tsv._scene.add(tsv._userLogBounds);

      } else {

        Logger.error(`[threeview] initScene - could not build user logging lines.`);
      }

    }

    /* for debugging of display configuration */

    tsv._hoverObject            = new Group();
    tsv._selectObject           = new Group();
    tsv._searchObject           = new Group();
    tsv._authoritiesObject      = new Group();
    tsv._authoritiesObjectHover = new Group();
    tsv._parkabilityObject      = new Group();

    tsv._scene.add(tsv._hoverObject);
    tsv._scene.add(tsv._selectObject);
    tsv._scene.add(tsv._searchObject);
    tsv._scene.add(tsv._authoritiesObject);
    tsv._scene.add(tsv._authoritiesObjectHover);
    tsv._scene.add(tsv._parkabilityObject);

    /* create the pseudo camera we use for panning and zooming */

    tsv._currentFocus = tsv.getScaledBound();

    tsv._panZoom      = new PanZoom(
      tsv._renderer,
      tsv._camera,
      new PanZoomOptions({
        minZoom:    tsv._camera.far,
        maxZoom:    tsv._camera.near,
        bound:      tsv.getScaledBound()
      })
    );

    tsv._panZoom.onEvent('*', (event: ViewerEvent): void => {

      /*
       * we need to watch for key and mouse events, but also pan and zoom, so
       * we can do appropriate updates to the x-axis/y-axis.
       *
       */

      tsv.handleEvent(event);

    });

    /* everything has been added to the graph, the extent should now be known */

    tsv.updateBound();

    Logger.debug('[threeview] initScene completed.');
  }

  lock(msg: string='') {

    Logger.debug('[threeview] locking ' + msg);

    // @ts-ignore
    $.LoadingOverlay('show', {
      text: msg,
      background: 'rgba(255, 255, 255, 0.85)',
      textColor: 'black',
      imageColor: 'black',
      fade: 400
    });

    // @ts-ignore
    $.LoadingOverlay('text', msg);
  }

  unlock(msg: string='') {

    Logger.debug('[threeview]  unlocking ' + msg);

    // @ts-ignore
    $.LoadingOverlay('text', msg);

    // @ts-ignore
    $.LoadingOverlay('hide');

  }

  /** @brief set the raw data model for this scenario, will chain to rendering a new graph */
  setScenario(scenario: Scenario = null, base: Scenario=null): boolean {

    Logger.debug('[threeview]', 'refreshing: ', scenario);

    this.viewLocked = !!this.ide.defaultStringLineView;

    if (scenario == null) {

      this.loadError = 'No Scenario.';

      this.clear();

      return true;
    }

    if (scenario.trains().size == 0) {

      Logger.error(`[threeview] setScenario - no trains found in scenario. Aborting render.`);

      this.loadError = 'No trains found in scenario.';

      return false;
    }

    /* save the model */

    this.loadError = '';

    if (base == null) {

      /* they are forcing the base (UTC) model, and the timezone is UTC as well (default dcase) */

      this._scenario    = scenario;
      this._utcScenario = scenario;

      /*
       * if the timezone is somehow not UTC, and they didn't provide but the base and the
       * adjusted models, then we try try auto-track...
       *
       */

      if (this._timezone != 'UTC') {

        this._scenario = this._utcScenario.timezone(this._timezone);
      }

    } else {

      /* base is the new utc model, while scenario has been adjusted for timezone. */

      this._scenario    = scenario;
      this._utcScenario = base;
    }

    $('.command-bar-title').html(this._scenario._tag);

    /* create the three.js viewer in #three-string-viewer-container ... */

    this.init();

    /* start the animation loop, we are *always* animating */

    this.animate();

    this._searchManager.history(this._scenario.trainMovements());

    return true;
  }

  /** @brief helper to auto-zoom/pan the camera so the given bound (world space) is just fitting on screen */
  focusCamera(box: Box2): void {

    if (this.viewLocked) {

      Logger.debug('[threeview] focusCamera, view is locked, skipping refocus');

      return ;
    }

    Logger.debug('[threeview] focusCamera: bound: ', box);

    if (!this._panZoom) {

      return ;
    }

    this._panZoom.zoomToBox(box);

    this.refreshVisuals();
  }

  /** @brief helper to convert angles to radians */
  toRadians(angle: number): number {

    return angle * (Math.PI / 180);
  }

  /** @brief convert a screen point (i.e. mouse position) to the same point in world space */
  screenToWorld(x: number, y: number): [number, number] {

    if (!this._panZoom) {

      return [0,0];
    }

    let {wx, wy} = this._panZoom.screenToWorld(x,y);

    return [wx, wy];
  }

  /** @brief inverse of screenToWorld */
  worldToScreen(x: number, y: number): [number, number] {

    if (!this._panZoom) {

      return [0,0];
    }

    let {sx, sy} = this._panZoom.worldToScreen(x,y);

    return [sx, sy];
  }

  /** @brief our resize handler, make sure the perspective camera is updated */
  @HostListener('window:resize')
  public resize(): void {

    if (this._drawingSurface == null) {

      /* nothing to do yet */

      return ;
    }

    Logger.debug('[threeview] resizing...');

    const container: any              = document.getElementById(`three-string-viewer-container_${this.uuid}`);
    const         b: ViewMemento      = this.viewMemento();

    if (($(container).width() === 0) || ($(container).height() === 0)) {

      /* ignore requests to go to zero size */

      return ;
    }

    if (($(container).width() === this._width) && ($(container).height() === this._height)) {

      /* ignore requests to resize to what we already are */

      return ;
    }

    this._width             = container.getBoundingClientRect().width;
    this._height            = container.getBoundingClientRect().height;

    /* new screen width or height means the viewing project changes, update the pan/zoom ... */

    this.updateBound();

    /* set our new geometry */

    this._drawingSurface.width        = this._width;
    this._drawingSurface.height       = this._height;

    /* update ths rendering pipeline */

    this._camera.aspect               = this._width / this._height;
    this._camera.updateProjectionMatrix();

    this._renderer.setSize( this._width, this._height );
    this._renderer.render( this._scene, this._camera );

    $(`#three-string-grid-canvas_${this.uuid}`).width(this._width);
    $(`#three-string-grid-canvas_${this.uuid}`).height(this._height);

    const grid: HTMLCanvasElement = document.getElementById(`three-string-grid-canvas_${this.uuid}`) as HTMLCanvasElement;
    grid.width  = this._width;
    grid.height = this._height;

    this._yAxis.resize();
    this._xAxis.resize();

    this.focus(b);
  }

  /** @brief helper to find any train movement right under the given mouse position */
  public overMovement(world: Vector2): Movement {

    /* find movements nearby */

    const [tx, ty]            = [world.x * 3600.0 / this._timeScale, world.y / this._distanceScale];
    const matches: Movement[] = this._scenario.near(tx, ty);
    let     hit: Movement     = null;

    /* walk through the possibles and look for collision, just use the first one we hit */

    for (const move of matches) {

      /* consider the next move, first generate its lines (in world space) */

      const lines: any[] = move.lines();

      for (const [x1, y1, x2, y2] of lines) {

        const sx: number = x1 / 3600.0 * this._timeScale;
        const sy: number = y1 * this._distanceScale;
        const ex: number = x2 / 3600.0 * this._timeScale;
        const ey: number = y2 * this._distanceScale;

        /*
         * First project everything back to screen space, so we are dealing in pixels
         * and not the "projected" distances.  Now "extrude" the line to a rectangle of
         * some thickness.  This gives us a nice pad that we check for intersection with,
         * and its in screen space (pixels), so the pad size is always guaranteed to be
         * the same.
         *
         * @link https://forum.libcinder.org/topic/smooth-thick-lines-using-geometry-shader
         *
         */

        /* use the hover pad width, in screen space, so that the pad is always comfortably wide */

        const thick: number = this._hoverPad;

        /* project everything back to screen space */

        const [vsx, vsy]    = this.worldToScreen(sx, sy);
        const [vex, vey]    = this.worldToScreen(ex, ey);
        const [vwx, vwy]    = this.worldToScreen(world.x, world.y);

        /* figure out the line and its normal vector */

        const   p1: Vector2 = new Vector2(vsx, vsy);
        const   p2: Vector2 = new Vector2(vex, vey);

        const line: Vector2 = p2.clone();

        line.sub(p1);

        let normal: Vector2 = null;

        /** @link https://stackoverflow.com/questions/1243614/how-do-i-calculate-the-normal-vector-of-a-line-segment */

        /* make sure we orient, so we can build a clockwise polygon below... */

        if (vey < vsy) {

          normal = new Vector2(-line.y, line.x).normalize();

        } else {

          normal = new Vector2(line.y, -line.x).normalize();
        }

        /* get half size normal */

        normal.multiplyScalar(thick);

        /* build the points in clockwise order, so they form a regular polygon */

        const a: Vector2 = p1.clone().sub(normal);
        const b: Vector2 = p2.clone().sub(normal);
        const c: Vector2 = p2.clone().add(normal);
        const d: Vector2 = p1.clone().add(normal);

        const points: {x: number, y: number}[] = [
          {x: a.x, y: a.y},
          {x: b.x, y: b.y},
          {x: c.x, y: c.y},
          {x: d.x, y: d.y}
        ];

        /* what is the bounding box of the E-Z click pad? */

        let minX: number = 9999999;
        let minY: number = 9999999;
        let maxX: number = 0;
        let maxY: number = 0;

        for (const p of points) {

          if (p.x < minX) {
            minX = p.x;
          }

          if (p.x > maxX) {
            maxX = p.x;
          }

          if (p.y < minY) {
            minY = p.y;
          }

          if (p.y > maxY) {
            maxY = p.y;
          }

        }

        if (vwx < minX || vwx > maxX || vwy < minY || vwy > maxY) {

          /* not even close */

          continue;
        }

        /*
         * now check for this point being in this polygon
         *
         * @link https://stackoverflow.com/questions/217578/how-can-i-determine-whether-a-2d-point-is-within-a-polygon
         * @link https://stackoverflow.com/questions/39853481/is-point-inside-polygon
         *
         */

        points.reverse();

        let ok: boolean = false;
        let last       = points[points.length - 1];

        for (const v of points) {

          if ( ( vwy - last.y ) * ( last.y - v.y ) < 0) {

            const t: number = ( vwy - last.y ) / ( v.y - last.y );
            const x: number = t * ( v.x - last.x ) + last.x;

            if ( x >= vwx ) {

              ok = !ok;
            }

          } else {

            if ( vwy === last.y && vwx < last.x && v.y > vwy ) {

              ok = !ok;
            }

            if ( vwy === v.y && vwx < v.x && last.y > vwy ) {

              ok = !ok;
            }
          }

          last = v;
        }

        if (ok) {

          /* click! */

          hit = move;

          break;
        }

      } /* end of line walk */

      if (hit != null) {

        break;
      }

    } /* end of move walk */

    return hit;
  }

  /** time cursor position or mode or both has changed, and we now want to show authority extras at this world time */
  showAuthorities(wt: number) {

    this._authoritiesObject.visible      = true;
    this._authoritiesObjectHover.visible = true;

    if (wt == this._authoritiesWT) {

      Logger.debug('[threeview] (cached) authorities', this._authoritiesWT);

      return ;
    }

    /* we have to build a new mesh; fetch the authorities then convert to colored interlinks */

    this._authoritiesWT   = wt;

    Logger.debug('[threeview] finding authorities...', this._authoritiesWT);

    /*
     * first we have to find the trains that we want the intersecting authorities of; i.e. the authorities
     * around this time in the simulation; to get that, we intersection the vertical line (the time position)
     * with the train lines to find the intersecting movements...and from movements we can go up to the parent
     * (the train).  This gives the set of trains involved at this time point.
     *
     */

    const sx: number = this._authoritiesWT * 3600.0;
    const sy: number = 0;
    const ex: number = this._authoritiesWT * 3600.0;
    const ey: number = this._dataHeight;

    const hits: Movement[] = this._scenario.intersects(sx, sy, ex, ey);
    const trains: Train[]  = [];

    this._selectionManager.select(null, false);

    hits.forEach((hit: Movement) => {

      this._selectionManager.select(hit._parent, true);
    });

    hits.forEach((hit: Movement) => {

      this._selectionManager.select(hit._parent, true);

      trains.push(hit._parent._train);
    });

    this._authorityManager.setTrains(trains);

    this.drawExtras(true);

  }

  /** time cursor position or mode or both has chnaged, and we now want to show authority extras at this world time */
  showParkability(wt: number) {

    this._parkabilityObject.visible = true;

    if (wt == this._parkabilityWT) {

      Logger.debug('[threeview] (cached) parkability', this._parkabilityWT);

      return ;
    }

    /* we have to build a new mesh; for selected trains, show the parkability interlinks */

    this._parkabilityWT = wt;

    Logger.debug('[threeview] finding parkability...', this._authoritiesWT);

    /*
     * first we have to find the trains that we want; we intersect the vertical line (the time position)
     * with the train lines to find the intersecting movements...and from movements we can go up to the parent
     * (the train).  This gives the set of trains involved at this time point.
     *
     */

    const sx: number = this._parkabilityWT * 3600.0;
    const sy: number = 0;
    const ex: number = this._parkabilityWT * 3600.0;
    const ey: number = this._dataHeight;

    const hits: Movement[] = this._scenario.intersects(sx, sy, ex, ey);
    const trains: Train[]  = [];

    this._selectionManager.select(null, false);

    hits.forEach((hit: Movement) => {

      this._selectionManager.select(hit._parent, true);

      trains.push(hit._parent._train);
    });

    this._parkabilityManager.setTrains(trains);

    this.drawExtras(true);
  }

  highlightMove(move: Movement): void {

    Logger.debug('[threeview] highlightMove: ', move);

    /* configure styling ... */

    let tsv: ThreeStringViewer = this;
    const train: Train         = move._parent._train;
    let opacity: number        = 0.4;
    const  converter: Color    = new Color('#000000');
    let thickness: number      = tsv._hoverPad;
    const trainColor: string   = tsv.trainStylePicker(train);

    converter.set(trainColor);

    if (tsv._colorMode === StringColorMode.TRACK) {

      /* in track coloring mode, we have to color  each segment */

      converter.set(tsv.trackStylePicker(move));
    }

    /* build a highlight line, but just for the move they are hovering over */

    let lineMesh: Mesh = (new MeshLineBuilder())
      .setColor(converter)
      .setThickness(thickness)
      .setHistory(move._parent)
      .setMoves([move])
      .setOpacity(opacity)
      .build(tsv._width, tsv._height);

    lineMesh.geometry.name = 'highlight';

    tsv._hoverObject.add(lineMesh);
  }

  /** @brief left mouse click handler - select a train movement line (whole thing) */
  onClick(event: MouseEvent): void {

    if (this._drawingSurface ==  null) {

      /* nothing to do yet */

      return ;
    }

    /* remove whatever was previously highlighted */

    this._hoverObject.remove(...this._hoverObject.children);

    /* convert browser mouse co-ords to canvas position */

    this._mouseCanvasX = event.offsetX;
    this._mouseCanvasY = event.offsetY;

    const [wx, wy]     = this.screenToWorld( this._mouseCanvasX,  this._mouseCanvasY );
    let hit: Movement  = this.overMovement(new Vector2(wx, wy));

    /* check for click on a decision point */

    const dpHit: {dp: DecisionPoint, train: Train} = this.mouseOverDecisionPoint(hit);

    if (dpHit != null) {

      /* currently no click action for decision points */


    }

    /* usual train movement line segment hovering */

    this._hoverMove     = hit;
    this._hoverEvent    = event;

    if (hit == null) {

      /* nothing to do, not over a segment */

      this.movementHover.emit({move: null, event});

      /* clicking the background means clearing the selection */

      this._selectionManager.select(null, false);

      return ;
    }

    /*
     * if over a train history line when they clicked, then it counts as a selection action
     *
     * @note CTRL + <click> is like windows; if not arleady selected, append, if selected
     * remove from  selection.
     *
     */

    this._selectionManager.select(hit._parent, event.ctrlKey);

  }

  /** @brief handle key bindings for the graph */
  onKeyPress(event: KeyboardEvent): void {

    /*
     * @link https://alligator.io/angular/binding-keyup-keydown-events/
     * @link https://stackoverflow.com/questions/35966965/what-typescript-type-is-angular2-event
     *
     */

    if (this._drawingSurface ==  null) {

      /* nothing to do yet */

      return ;
    }

    const shifted: boolean  = event.shiftKey;
    const    ctrl: boolean  = event.ctrlKey;
    const    keys: string[] = [
      'f1',
      'f3',
      'f11',
      'tab',
      'enter',
      'f',
      's',
      'c',
      'd',
      'v',
      'l',
      'arrowup',
      'arrowdown',
      'arrowleft',
      'arrowright'
    ];
    const     key: string   = event.key.toLowerCase();

    if (!keys.some((k) => k === key)) {

      /* not a key we're handling */

      return ;
    }

    if ((key === 'tab') && (!shifted)) {

      /* pop the selection manager, and focus on the previous selection item */

      Logger.debug('[threeview] next selection ...');

      this._searchManager.hide();
      this._selectionManager.focusNext();

    } else if ((key === 'tab') && (shifted)) {

      /* pop the selection manager, and focus on the previous selection item */

      Logger.debug('[threeview] previous selection ...');

      this._searchManager.hide();
      this._selectionManager.focusPrevious();

    } else if ((key === 'f3') && (!shifted)) {

      /* pop the selection manager, and focus on the previous selection item */

      Logger.debug('[threeview] next search result ...');

      this._selectionManager.hide();
      this._searchManager.focusNext();

    } else if ((key === 'f3') && (shifted)) {

      /* pop the selection manager, and focus on the previous selection item */

      Logger.debug('[threeview] previous search result ...');

      this._selectionManager.hide();
      this._searchManager.focusPrevious();

    } else if ((key === 'f') && (ctrl)) {

      /* pop the search input and focus on it */

      Logger.debug('[threeview] searching ...');

      this._selectionManager.hide();

      if (this._searchManager.alreadyInFccus()) {

        this._searchManager.unfocus();

      } else {

        this._searchManager.focus();
      }

    } else if ((key === 's') && (ctrl)) {

      /* pop the search input and focus on it */

      Logger.debug('[threeview] selection ...');

      this._searchManager.hide();

      if (this._selectionManager.visible()) {

        this._selectionManager.hide();

      } else {

        this._selectionManager.show();
      }

    } else if ((key === 'c') && (ctrl)) {

      this.onColorModeToggle();

    } else if ((key === 'v') && (ctrl)) {

      this.onHUDToggle();

    } else if ((key === 'l') && (ctrl)) {

      this.onViewLock();

    } else if ((key === 'd') && (ctrl)) {

      this.onNightModeToggle();

    } else if (key === 'f11') {

      this.toggleFullScreen();

    } else if ((key === 'arrowup') && ctrl) {

      this.scaleDistanceTo(Math.floor(this._distanceScale) + 1);

    } else if ((key === 'arrowdown') && ctrl) {

      this.scaleDistanceTo(Math.floor(this._distanceScale) - 1);

    } else if ((key === 'arrowleft') && ctrl) {

      this.scaleTimeTo(Math.floor(this._timeScale) - 1);

    } else if ((key === 'arrowright') && ctrl) {

      this.scaleTimeTo(Math.floor(this._timeScale) + 1);

    }

    /* we handled it */

    event.preventDefault();
  }

  /** @brief we already know the mouse is hovering near a train movement line, but is it over one of the endpoints? */
  mouseOverDecisionPoint(move: Movement): {dp: DecisionPoint, train: Train} {

    if (move == null) {

      /* nothing to do */

      return;
    }

    const bound: number       = 10;
    let target: DecisionPoint = null;

    /* what is the bound of the movement in screen space? */

    let [left, bottom] = this.worldToScreen(
      move._qtX / 3600 * this._timeScale,
      move._qtY * this._distanceScale
    );

    let [right, top] = this.worldToScreen(
      (move._qtX + move._qtWidth) / 3600 * this._timeScale,
      (move._qtY + move._qtHeight) * this._distanceScale
    );

    /*
     * before we check for the bounding decision points, check to see if they are hovering over
     * the "elbow"; this only happens when the train has delays, and the arrival isn't also the
     * departure time.  If they are hovering over the elbow, we can use the decision point
     * of the move because that is the one they arrived at, and where they are delayed.
     *
     */

    if (move.hasMidpoint()) {

      let [mx, my] = move.midpoint();

      let [midx, midy] = this.worldToScreen(
        mx / 3600 * this._timeScale,
        my * this._distanceScale
      );

      if ((Math.abs(this._mouseCanvasX - midx) < bound) && (Math.abs(this._mouseCanvasY - midy) < bound)) {

        /* yes, they are hovering over the elbow */

        return {dp: move._dp, train: move._parent._train};
      }
    }

    /* is the cursor "near" either corner? */

    if (((Math.abs(this._mouseCanvasX - left) < bound) && (Math.abs(this._mouseCanvasY - bottom) < bound)) ||
        ((Math.abs(this._mouseCanvasX - right) < bound) && (Math.abs(this._mouseCanvasY - bottom) < bound))) {

      /* yes, it's near the lower distance and lower time decision point */

      /* is one of the decision points at that corner? */

      [move._track._before, move._track._after].forEach((dp: DecisionPoint) => {

        if (!dp) {

          return ;
        }

        if (Math.abs(dp._mileage - move._qtY) < 0.001)  {

          target = dp;
        }

      });

    } else if (((Math.abs(this._mouseCanvasX - right) < bound) && (Math.abs(this._mouseCanvasY - top) < bound)) ||
               ((Math.abs(this._mouseCanvasX - left) < bound) && (Math.abs(this._mouseCanvasY - top) < bound))) {

      /* yes, it's near the higher distance and higher time decision point */

      /* is one of the decision points at that corner? */

      [move._track._before, move._track._after].forEach((dp: DecisionPoint) => {

        if (!dp) {

          return ;
        }

        if (Math.abs(dp._mileage - (move._qtY + move._qtHeight)) < 0.001) {

          target = dp;
        }

      });

    }

    /* pass back whatever we hit (or didn't) */

    return {dp: target, train: move._parent._train};
  }

  /** @brief helper to return the movement currently under the mouse...if there is one */
  mouseOverMovement(): Movement {

    if (this._drawingSurface ==  null) {

      /* nothing to do yet */

      return null;
    }

    const [wx, wy]    = this.screenToWorld( this._mouseCanvasX,  this._mouseCanvasY );

    /* Logger.debug(`[threeview] mouseOverMovement: checking x=${this._mouseCanvasX}, y=${this._mouseCanvasY}, wx=${wx}, wy=${wy}...`); */

    let hit: Movement = this.overMovement(new Vector2(wx, wy));

    if (hit != null) {

      /* Logger.debug('[threeview] mouseOverMovement: HIT: ', hit); */

      let lines: any[] = hit.lines();

      /*
       * before we draw the highlight, be sure the line is visible; if we have a clip limit
       * set, if the whole thing is to the right of the clip limit, then nothing to show!
       *
       */

      lines = lines.filter((coords: number[]) => {

        let clipped: boolean = (((coords[0] / 3600) >= this._clipX) && ((coords[2] / 3600) >= this._clipX));

        if (clipped) {

          /* Logger.debug(`[threeview] mouseOverMovement: CLIPPED! x1=${coords[0] / 3600}, x2=${coords[2] / 3600}, clipX=${this._clipX}`); */
        }

        return !clipped;
      });

      if (lines.length == 0) {

        /* Logger.debug(`[threeview] mouseOverMovement: ALL LINES WERE CLIPPED!`); */

        /* clipped! */

        this._hoverDecisionPoint = null;
        this._hoverMove          = null;
        this._hoverEvent         = null;

        return;
      }
    }

    /* check for hovering over a decision point */

    const dpHit: {dp: DecisionPoint, train: Train} = this.mouseOverDecisionPoint(hit);

    if ((dpHit != null) && (dpHit.dp != null)) {


      this._hoverDecisionPoint = null;

      /*
       * we're disabling DP hovering all together; as for high density graphs, it gets
       * more display than the link info...which makes using the links at high density
       * more difficult. SN-1718.  the hovered decision point doesn't get used anywhere
       * except for drawing the HUD information around the point...so just disabling
       * it entirely should have any negative impacts.
       *
       this._hoverDecisionPoint = dpHit;
       *
       */

      /*
       * don't null out the line segment hit if there is one; we don't want the update
       * of the decision point heads up display to be "either or" with the update of
       * the line highlighting. SN-1661
       *
      hit                      = null;
       *
       */

    } else {

      this._hoverDecisionPoint = null;
    }

    /* hovering over a train movement line? */

    this._hoverMove    = hit;
    this._hoverEvent   = null;

    return hit;
  }

  /** @brief update the current hovered over movement (if possible) */
  highlightCheck(): Movement {

    const hit: Movement   = this.mouseOverMovement();

    if (hit == null) {

      return null;
    }

    let lines: any[]                  = hit.lines();

    /*
     * before we draw the highlight, be sure the line is visible; if we have a clip limit
     * set, if the whole thing is to the right of the clip limit, then nothing to show!
     *
     */

    lines = lines.filter((coords: number[]) => {

      return !(((coords[0] / 3600) >= this._clipX) && ((coords[2] / 3600) >= this._clipX));

    });

    if (lines.length == 0) {

      /* clipped! */

      return null;
    }

    /* highlight this segment */

    this.highlightMove(hit);

    return hit;
  }

  onMouseOut(event: MouseEvent): void {

    /* cancel any hovering */

    if (this._drawingSurface == null) {

      /* nothing to do yet */

      return;
    }

    /* remove whatever was previously highlighted */

    this._hoverObject.remove(...this._hoverObject.children);

    /* convert browser mouse co-ords to canvas position */

    this._mouseCanvasX = event.offsetX;
    this._mouseCanvasY = event.offsetY;

    if (this._limitMode == TimeCursorMode.PARKABILITY) {

      if (this._parkabilityManager.hasHover()) {

        this._parkabilityManager.clearHover();

        this.drawExtras();
      }

      this.parkingHover.emit({link: null, allowed: false, event});

    } else  if (this._limitMode == TimeCursorMode.AUTHORITIES) {

      if (this._authorityManager.hasHover()) {

        this._authorityManager.clearHover();

        this.drawExtras();
      }

      this.authorityHover.emit({link: null, east: false, event});
    }

    /* cancel any decision point hover */

    this._hoverDecisionPoint = null;

    /* cancel any train segment hover */

    this._hoverMove          = null;
    this._hoverEvent         = null;

    this.movementHover.emit({move: null, event});
  }

  /** @brief when the mouse moves, we have to do things like re-draw the spike lines */
  onMouseMoved(event: MouseEvent): void {

    if (this._drawingSurface ==  null) {

      /* nothing to do yet */

      return ;
    }

    /* remove whatever was previously highlighted */

    this._hoverObject.remove(...this._hoverObject.children);

    /* convert browser mouse co-ords to canvas position */

    this._mouseCanvasX = event.offsetX;
    this._mouseCanvasY = event.offsetY;

    /* check for hovering over the extra info panels authority/parkability */

    if (this._limitMode == TimeCursorMode.PARKABILITY) {

      const [wx, wy]   = this.screenToWorld( this._mouseCanvasX,  this._mouseCanvasY );

      const link: {slot: InterLinkPark, allowed: boolean} = this._parkabilityManager.contains(wx,wy);

      if (link != null) {

        /* highlight that one */

        this._parkabilityManager.addHover(link.slot, link.allowed);

        this.drawExtras();

        /*
         * if we hovered of a valid allowed/denied box, emit an event for that, so the HUD
         * display the train/track info.
         *
         */

        this.parkingHover.emit({link: link.slot, allowed: link.allowed, event});

      } else {

        /* clear any highlight (if needed) */

        if (this._parkabilityManager.hasHover()) {

          this._parkabilityManager.clearHover();

          this.drawExtras();
        }


        this.parkingHover.emit({link: null, allowed: false, event});
      }

    } else if(this._limitMode == TimeCursorMode.AUTHORITIES) {

      const [wx, wy]   = this.screenToWorld( this._mouseCanvasX,  this._mouseCanvasY );

      const link: {slot: InterLinkAuth, east: boolean} = this._authorityManager.contains(wx,wy);

      if (link != null) {

        /* highlight that one */

        this._authorityManager.addHover(link.slot, link.east);

        this.drawExtras();

        /*
         * if we hovered of a valid allowed/denied box, emit an event for that, so the HUD
         * display the train/track info.
         *
         */

        this.authorityHover.emit({link: link.slot, east: link.east, event});

      } else {

        /* clear any highlight (if needed) */

        if (this._authorityManager.hasHover()) {

          this._authorityManager.clearHover();

          this.drawExtras();
        }


        this.authorityHover.emit({link: null, east: false, event});
      }

    }

    /* are they hovering over a train movement */

    const hit: Movement   = this.highlightCheck();

    this.movementHover.emit({move: hit, event});
  }

  onManageSelection() {

    Logger.debug('[threeview] managing selection...');

    this._searchManager.hide();

    if (this._selectionManager.visible()) {

      this._selectionManager.hide();

    } else {

      this._selectionManager.show();
    }

  }

  onGraphSearch() {

    Logger.debug('[threeview] popping search dialog...');

    this._selectionManager.hide();

    if (this._searchManager.visible()) {

      this._searchManager.hide();

    } else {

      this._searchManager.show();
    }
  }

  onAutoFitToggle() {

    Logger.debug('[threeview] view reset...');

    this.autoFit();

    this.refreshVisuals();
  }

  onNightModeToggle() {

    if (this._drawingSurface == null) {

      /* nothing to do yet */

      return ;
    }

    Logger.debug('[Threeview] switching print mode...');

    /* change command bar */

    let oldClass: string = 'night';
    let newClass: string = 'day';

    if (!this._nightMode) {

      oldClass        = 'day';
      newClass        = 'night';

      this._nightMode = true;

    } else {

      this._nightMode = false;
    }

    this._yAxis.setNightMode(this._nightMode);
    this._xAxis.setNightMode(this._nightMode);

    $('.command-bar-icon').removeClass(oldClass);
    $('.command-bar-icon').addClass(newClass);

    /* change title */

    $('.command-bar-title').removeClass(oldClass);
    $('.command-bar-title').addClass(newClass);

    $(`div#three-string-command-bar_${this.uuid}`).removeClass(oldClass);
    $(`div#three-string-command-bar_${this.uuid}`).addClass(newClass);

    $('.resizable-popup').removeClass(oldClass);
    $('.resizable-popup').addClass(newClass);

    $(`#xscale-picker-label_${this.uuid}`).removeClass(oldClass);
    $(`#xscale-picker-label_${this.uuid}`).addClass(newClass);

    $(`#yscale-picker-label_${this.uuid}`).removeClass(oldClass);
    $(`#yscale-picker-label_${this.uuid}`).addClass(newClass);

    this._selectionManager.nightMode(this._nightMode);
    this._searchManager.nightMode(this._nightMode);

    /* redraw the graph */

    const b: ViewMemento = this.viewMemento();

    this.lock();

    setTimeout( () => {

      this.init();

      this.focus(b);

      this.unlock();

    }, 500);

  }

  onGridModeToggle() {

    Logger.debug('[threeview]', 'switching grid mode...');

    this._gridMode = !this._gridMode;

    if (this._gridCurrent == null) {

      return ;
    }

    this._gridCurrent.visible = this._gridMode;

  }

  onSpikeModeToggle() {

    Logger.debug('[threeview] switching spike mode...');

    const canvas: HTMLCanvasElement = document.getElementById(`three-string-grid-canvas_${this.uuid}`) as HTMLCanvasElement;
    const context                   = canvas.getContext('2d');

    context.clearRect(0, 0, this._width, this._height);

    this._spikeLines = !this._spikeLines;
  }

  onHUDToggle() {

    if (this._drawingSurface == null) {

      /* nothing to do yet */

      return ;
    }

    Logger.debug('[threeview] switching HUD mode');

    this._simpleHUD = this._simpleHUD !== true;

    /* since we are always animated, nothing more to do, the animation loop will redraw */


  }

  onColorModeToggle() {

    if (this._drawingSurface == null) {

      /* nothing to do yet */

      return ;
    }

    Logger.debug('[threeview] switching color mode');

    if (this._colorMode === StringColorMode.TRAIN) {

      this._colorMode = StringColorMode.TRACK;

    } else {

      this._colorMode = StringColorMode.TRAIN;
    }

    /* redraw the graph */

    const b: ViewMemento = this.viewMemento();

    this.lock();

    setTimeout( () => {

      this.init();

      this.focus(b);

      this.unlock();

    }, 500);

  }

  onZoomOut() {

    if (!this._drawingSurface || this._panZoom) {

      /* nothing to do yet */

      return ;
    }

    this._panZoom.zoomOut();

    this.refreshVisuals();
  }

  onViewLock(): void {

    if (this.viewLocked) {

      /* remove the default zoom/position for string line viewers */

      Logger.debug('[threeview] removing default view position.');

      this.ide.stringLineView(null);

      this.viewLocked = false;

      return ;
    }

    /* set the new default zoom/position for string line viewers */

    Logger.debug('[threeview] setting default view position.');

    this.ide.stringLineView(this.viewMemento());

    this.viewLocked = true;
  }

  onZoomIn() {

    if (!this._drawingSurface || this._panZoom) {

      /* nothing to do yet */

      return ;
    }

    this._panZoom.zoomOut();

    this.refreshVisuals();
  }
}
